/* global __app_id, __initial_auth_token, __firebase_config */
import React, { useState, useEffect, useRef } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc,
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp, 
  increment,
  query,
  orderBy,
  limit
} from 'firebase/firestore';
import { 
  Search, ShieldAlert, Loader2, Trash2, Camera, 
  Zap, X, CheckCircle2, ShieldCheck, Globe,
  ThumbsUp, ThumbsDown, Gavel, DollarSign, ExternalLink, PartyPopper, 
  ChevronRight, ChevronDown, ChevronUp, History, LayoutDashboard, Copy, 
  AlertTriangle, TrendingUp, Briefcase, FileText, MessageSquare, Send, 
  Printer, Share2, BookOpen, Gift, Star, Award, Link as LinkIcon, QrCode, Lock,
  User, LogIn, LogOut, Car, Building2, ShoppingBag, Gem, Languages, Activity
} from 'lucide-react';

// --- BULLETPROOF CONFIGURATION & ENV HELPERS ---
const getEnvConfig = () => {
  try {
    if (typeof __firebase_config !== 'undefined' && __firebase_config) return JSON.parse(__firebase_config);
  } catch (e) {}
  
  // We use literal process.env references so Webpack can properly inject them on Netlify
  try {
    return {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "", 
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
      appId: process.env.REACT_APP_FIREBASE_APP_ID || ""
    };
  } catch (err) {
    return { apiKey: "", authDomain: "", projectId: "", storageBucket: "", messagingSenderId: "", appId: "" };
  }
};

const firebaseConfig = getEnvConfig();
// Bumped to v6 for the new ecosystem features
const appId = typeof __app_id !== 'undefined' ? __app_id : "haggler_prod_final_v6";

let envGeminiKey = "";
try {
  envGeminiKey = process.env.REACT_APP_GEMINI_API_KEY || "";
} catch (e) {}
const apiKey = envGeminiKey;

let app = null;
if (getApps().length > 0) {
  app = getApps()[0];
} else if (firebaseConfig && firebaseConfig.projectId) {
  app = initializeApp(firebaseConfig);
}

const auth = app ? getAuth(app) : null;
const db = app ? getFirestore(app) : null;

// ==============================================================================
// STRIPE PAYMENT LINKS
// ==============================================================================
const STRIPE_LINKS = {
  STARTER: "https://buy.stripe.com/7sY8wPgWYbiU6pebD0dfG02",
  PRO: "https://buy.stripe.com/28E28r6ikcmYeVKayWdfG03",
  ELITE: "https://buy.stripe.com/6oU9ATcGIaeQdRGbD0dfG04"
};

// ==============================================================================
// CREATOR / PROMO CODES
// ==============================================================================
const CREATOR_CODES = {
  'TIKTOK10': 10,
  'LAUNCH5': 5,
  'HAGGLERPRO': 20,
  'EARLYBIRD': 15
};

// --- IMAGE COMPRESSION HELPER ---
const generateThumbnail = (dataUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const MAX_WIDTH = 250;
      const scaleSize = Math.min(1, MAX_WIDTH / img.width);
      canvas.width = img.width * scaleSize;
      canvas.height = img.height * scaleSize;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.6));
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
};

// ==============================================================================
// APP UI LOCALIZATION DICTIONARY
// ==============================================================================
const UI_TEXT = {
  English: {
    win: "Win the",
    deal: "Deal.",
    engine: "The Universal Negotiation Engine",
    buy: "Buy",
    resell: "Resell",
    upload: "Upload Screenshots",
    drop: "Drop photos, descriptions, Zillow listings, Carfax reports, or price sheets",
    analyze: "ANALYZE DEAL",
    init: "INITIALIZING...",
    target: "Target Offer",
    report: "View Haggler Report",
    deductions: "Identified Deductions",
    more: "More Deductions",
    response: "Seller Response (Free Follow-up)",
    paste: "Paste what the seller said back...",
    new: "New Audit Sequence",
    vault: "Market",
    vaultSpan: "Vault",
    empty: "No scan data found.",
    bought: "Bought For ($)",
    sold: "Sold For ($)",
    profit: "Net Profit",
    feed: "Global",
    feedSpan: "Feed",
    appraisals: "Live Market Appraisals",
    asking: "Asking Price",
    trueVal: "True Value",
    navHaggle: "Haggle",
    navVault: "Vault",
    navFeed: "Feed",
    navRewards: "Rewards",
    navRefuel: "Refuel",
    securityWarning: "Security Warning",
    askingLabel: "Asking:",
    targetLabel: "Target:",
    grade: "Grade",
    tapToExpand: "Tap to expand",
    officialReport: "Official Haggler Report",
    targetAsset: "Target Asset",
    adjustments: "Market Condition Adjustments",
    flipperMargin: "Flipper Margin (30% Resell Protection)",
    targetValuation: "Target Market Valuation",
    sources: "Verified Sources",
    auditId: "Official Audit ID",
    generated: "Generated:",
    generatedBy: "Generated by LUNCHBOX Core Intelligence",
    print: "Print",
    copy: "Copy",
    share: "Share",
    close: "Close",
    scannerExtracting: "Extracting metadata...",
    scannerRunning: "Running live market comps...",
    scannerIsolating: "Isolating asset flaws...",
    scannerCross: "Cross-referencing historical data...",
    scannerCalc: "Calculating true value..."
  },
  Spanish: {
    win: "Gana el",
    deal: "Trato.",
    engine: "El Motor Universal de Negociación",
    buy: "Comprar",
    resell: "Revender",
    upload: "Subir Capturas",
    drop: "Sube fotos, descripciones, listados Zillow, reportes Carfax o listas de precios",
    analyze: "ANALIZAR TRATO",
    init: "INICIALIZANDO...",
    target: "Oferta Objetivo",
    report: "Ver Reporte Haggler",
    deductions: "Deducciones Identificadas",
    more: "Más Deducciones",
    response: "Respuesta del Vendedor (Seguimiento)",
    paste: "Pega lo que respondió el vendedor...",
    new: "Nueva Secuencia de Auditoría",
    vault: "Bóveda del",
    vaultSpan: "Mercado",
    empty: "No se encontraron datos.",
    bought: "Comprado Por ($)",
    sold: "Vendido Por ($)",
    profit: "Ganancia Neta",
    feed: "Feed",
    feedSpan: "Global",
    appraisals: "Tasaciones en Vivo",
    asking: "Precio Pedido",
    trueVal: "Valor Real",
    navHaggle: "Negociar",
    navVault: "Bóveda",
    navFeed: "Feed",
    navRewards: "Premios",
    navRefuel: "Recargar",
    securityWarning: "Aviso de Seguridad",
    askingLabel: "Precio:",
    targetLabel: "Objetivo:",
    grade: "Grado",
    tapToExpand: "Toca para ampliar",
    officialReport: "Reporte Oficial de Haggler",
    targetAsset: "Activo Objetivo",
    adjustments: "Ajustes del Mercado",
    flipperMargin: "Margen de Reventa (30% Protección)",
    targetValuation: "Valor de Mercado Objetivo",
    sources: "Fuentes Verificadas",
    auditId: "ID de Auditoría Oficial",
    generated: "Generado:",
    generatedBy: "Generado por LUNCHBOX Core Intelligence",
    print: "Imprimir",
    copy: "Copiar",
    share: "Compartir",
    close: "Cerrar",
    scannerExtracting: "Extrayendo metadatos...",
    scannerRunning: "Buscando comparativas en vivo...",
    scannerIsolating: "Aislando defectos del activo...",
    scannerCross: "Cruzando datos históricos...",
    scannerCalc: "Calculando valor real..."
  },
  French: {
    win: "Gagnez la",
    deal: "Négociation.",
    engine: "Le Moteur de Négociation Universel",
    buy: "Acheter",
    resell: "Revendre",
    upload: "Télécharger Captures",
    drop: "Déposez des photos, descriptions, annonces Zillow, rapports Carfax ou prix",
    analyze: "ANALYSER",
    init: "INITIALISATION...",
    target: "Offre Cible",
    report: "Voir le Rapport",
    deductions: "Déductions Identifiées",
    more: "Plus de Déductions",
    response: "Réponse du Vendeur (Suivi)",
    paste: "Collez la réponse du vendeur ici...",
    new: "Nouvelle Analyse",
    vault: "Coffre",
    vaultSpan: "Fort",
    empty: "Aucune donnée trouvée.",
    bought: "Acheté Pour ($)",
    sold: "Vendu Pour ($)",
    profit: "Bénéfice Net",
    feed: "Flux",
    feedSpan: "Global",
    appraisals: "Évaluations en Direct",
    asking: "Prix Demandé",
    trueVal: "Valeur Réelle",
    navHaggle: "Négocier",
    navVault: "Coffre",
    navFeed: "Flux",
    navRewards: "Récompenses",
    navRefuel: "Recharger",
    securityWarning: "Alerte de Sécurité",
    askingLabel: "Prix:",
    targetLabel: "Cible:",
    grade: "Note",
    tapToExpand: "Appuyez pour agrandir",
    officialReport: "Rapport Officiel Haggler",
    targetAsset: "Actif Cible",
    adjustments: "Ajustements du Marché",
    flipperMargin: "Marge de Revente (30% Protection)",
    targetValuation: "Évaluation Cible du Marché",
    sources: "Sources Vérifiées",
    auditId: "ID d'Audit Officiel",
    generated: "Généré:",
    generatedBy: "Généré par LUNCHBOX Core Intelligence",
    print: "Imprimer",
    copy: "Copier",
    share: "Partager",
    close: "Fermer",
    scannerExtracting: "Extraction des métadonnées...",
    scannerRunning: "Recherche de comparatifs en direct...",
    scannerIsolating: "Isolement des défauts...",
    scannerCross: "Croisement des données historiques...",
    scannerCalc: "Calcul de la valeur réelle..."
  },
  Mandarin: {
    win: "赢得",
    deal: "交易",
    engine: "通用谈判引擎",
    buy: "购买",
    resell: "转售",
    upload: "上传截图",
    drop: "拖放照片、描述、Zillow 列表、Carfax 报告或价格表",
    analyze: "分析交易",
    init: "初始化中...",
    target: "目标报价",
    report: "查看 Haggler 报告",
    deductions: "已识别的扣减",
    more: "更多扣减",
    response: "卖家回复 (免费跟进)",
    paste: "粘贴卖家的回复...",
    new: "新的审计序列",
    vault: "市场",
    vaultSpan: "金库",
    empty: "未找到扫描数据。",
    bought: "买入价 ($)",
    sold: "卖出价 ($)",
    profit: "净利润",
    feed: "全球",
    feedSpan: "动态",
    appraisals: "实时市场评估",
    asking: "要价",
    trueVal: "真实价值",
    navHaggle: "砍价",
    navVault: "金库",
    navFeed: "动态",
    navRewards: "奖励",
    navRefuel: "充值",
    securityWarning: "安全警告",
    askingLabel: "要价:",
    targetLabel: "目标:",
    grade: "评分",
    tapToExpand: "点击展开",
    officialReport: "Haggler 官方报告",
    targetAsset: "目标资产",
    adjustments: "市场状况调整",
    flipperMargin: "转售利润 (30% 保护)",
    targetValuation: "目标市场估值",
    sources: "验证来源",
    auditId: "官方审计 ID",
    generated: "生成时间:",
    generatedBy: "由 LUNCHBOX Core Intelligence 生成",
    print: "打印",
    copy: "复制",
    share: "分享",
    close: "关闭",
    scannerExtracting: "提取元数据中...",
    scannerRunning: "运行实时市场对比...",
    scannerIsolating: "隔离资产缺陷...",
    scannerCross: "交叉引用历史数据...",
    scannerCalc: "计算真实价值..."
  }
};

// ==============================================================================
// ACADEMY CONTENT DATABASE (SEO OPTIMIZED)
// ==============================================================================
const ACADEMY_ARTICLES = [
  {
    id: "how-to-answer-is-this-still-available",
    title: "How to Answer \"Is This Still Available?\" on Facebook Marketplace",
    preview: "Stop getting ghosted. Learn the psychological trick to instantly transition a lazy buyer into a committed deal.",
    content: "If you've ever sold anything on Facebook Marketplace, Craigslist, or OfferUp, you know the frustration. Your phone buzzes with a new message: \"Is this still available?\" You reply \"Yes!\" and then... dead silence. You've just been ghosted.\n\nThis automated auto-response button ruins more deals than it starts. Buyers accidentally bump the button while scrolling, or they send it to ten different sellers just to bookmark the items they like. The secret to selling your used items faster is breaking this automated loop.\n\nNever just reply with a simple \"Yes.\" You need to immediately transition the buyer into a commitment phase. The best response to \"Is this still available?\" is a question that requires a logistical answer. Reply with: \"Yes it is! When were you hoping to come take a look?\" or \"Yes! I am located in [Your City], are you able to pick it up today?\"\n\nIf you are the buyer sending the message, do not use the auto-fill text. Send a custom message: \"I see this is listed for $500, I have cash in hand and can pick it up at 5 PM.\" By bypassing the generic greeting, you prove you are a serious buyer, and sellers will often accept a lower offer just to deal with someone reliable."
  },
  {
    id: "the-local-deal-blueprint-how-to-negotiate-safely",
    title: "The Local Deal Blueprint: How to Negotiate Safely",
    preview: "Securing the best price means nothing if the actual exchange goes poorly. Master the art of the safe local meetup.",
    content: "Conducting a transaction from an online classifieds site requires preparation. Whether you are hunting for used cars, cheap furniture, or electronics, securing the best price means nothing if the actual exchange goes poorly.\n\nAlways dictate the terms of the meeting. Never invite strangers to your home address unless absolutely necessary for large items. Suggest a well-lit, public location with surveillance cameras. Many local police departments now have designated \"Safe Exchange Zones\" specifically established for Facebook Marketplace and Craigslist transactions.\n\nWhen it comes to the actual negotiation, use the \"Flaw Isolation\" technique. Instead of arbitrarily demanding $100 off the asking price, point to a specific issue—like a scratch, missing cable, or need for new tires—and assign a dollar value to it. Saying \"Because I'll need to replace the tires for $200, my offer is $3,800\" is logically sound and much harder for a seller to reject than a random lowball. This is exactly why using an AI negotiation tool like Haggler to find live market comps and assess mechanical flaws gives you an unfair advantage in every deal."
  },
  {
    id: "the-tire-kicker-defense-spotting-fake-buyers",
    title: "The Tire Kicker Defense: Spotting Fake Buyers",
    preview: "Don't waste time on buyers who will never show up. Learn the 3 red flags of a notorious marketplace 'Tire Kicker'.",
    content: "Every seller has dealt with them. The buyer who asks a million questions, demands extra photos, negotiates the price down to the penny, and then... completely ghosts you on the day of the meetup. These are \"Tire Kickers.\"\n\nTo protect your time and spot them early, watch for these massive red flags:\n\n1. They negotiate aggressively before asking about the condition of the item.\n2. They refuse to give a firm pickup time or location, keeping their answers vague (\"sometime this weekend\").\n3. Their profile is brand new, completely locked down, or has zero marketplace history.\n\nThe best defense is the \"Soft Close.\" Once you agree on a price, say: \"Great. I have two other people asking about it, but I will pull the listing and hold it for you if you can commit to meeting at [Location] at [Time] today. Does that work?\" \n\nIf they hesitate or make excuses, move on immediately to the next buyer in your inbox."
  },
  {
    id: "how-to-flip-furniture-for-profit",
    title: "Flipping Furniture: The High-Margin Side Hustle",
    preview: "Wood furniture flipping is incredibly lucrative if you know exactly what pieces to target and how to lowball effectively.",
    content: "Flipping furniture on Facebook Marketplace and OfferUp is one of the most accessible side hustles today. The secret isn't just in the sanding and painting—it's in the initial purchase strategy.\n\nYou should specifically target solid wood pieces (dressers, nightstands, and buffets) that have superficial visual damage. Scratches, water rings, faded edges, and ugly 1990s orange stain are your best friends. They scare off average buyers but are easily fixed by a flipper.\n\nWhen negotiating, use those visual flaws to drive the price into the dirt, even if you plan to sand them away anyway. Use Haggler to input the exact flaws (e.g., \"deep top scratches, requires stripping\") and let the AI generate a logical lowball script. A $40 solid wood dresser can easily sell for $250+ after a weekend of prep, premium paint, and new modern hardware."
  },
  {
    id: "facebook-marketplace-scams-to-avoid",
    title: "Top 3 Facebook Marketplace Scams to Avoid",
    preview: "Protect your cash and your identity. These are the top three scams currently targeting buyers and sellers online.",
    content: "Marketplaces are filled with legitimate deals, but scammers are always evolving. Here are the top three scams to actively avoid:\n\n1. The \"Google Voice Verification\" Scam:\nYou list an item. A buyer immediately texts you saying they want to verify you are a real person and will send you a 6-digit code to read back to them. NEVER GIVE THEM THE CODE. They are trying to hijack your phone number to set up a fraudulent Google Voice account to scam others.\n\n2. The Overpayment/Fake Check Scam:\nA buyer sends you a check or fake Zelle email for more than the asking price. They apologize for the \"mistake\" and ask you to refund the difference via Zelle, CashApp, or Venmo. The check they sent you will inevitably bounce, and your real money will be gone permanently.\n\n3. The Zelle Upgrade Scam:\nYou get an email claiming you need a \"Zelle Business Account\" to receive a buyer's payment, and you must pay a fee to upgrade. Zelle does not work this way. This is a fake, spoofed email generated by the scammer to steal your upgrade fee."
  },
  {
    id: "the-kelly-blue-book-myth",
    title: "The Kelly Blue Book Myth: Pricing Used Cars on Marketplace",
    preview: "KBB is often wildly inaccurate for private party sales. Learn how to pull true live comps instead.",
    content: "Many buyers and sellers treat Kelly Blue Book (KBB) as the ultimate authority on used car prices. However, KBB is often weeks or months behind actual market trends, especially in highly volatile used car markets.\n\nSellers often use the 'Excellent Condition' dealer retail price to justify their asking price for a private party sale. As a buyer, this is your biggest opening for negotiation. Private party sales should never command dealer retail prices because they lack dealer warranties, inspections, and financing options.\n\nThe only true measure of a car's value is what similar cars are selling for right now in your specific zip code. Always search for the exact make, model, year, and mileage on Facebook Marketplace and sort by 'Recently Listed'. If you see three similar cars listed for $5,000, $5,200, and $5,500, a seller asking $7,000 based on KBB is completely disconnected from reality.\n\nUse Haggler to pull live market data and present the seller with actual local comps. Say: 'I know KBB says X, but there are three identical cars in our area listed around $5,000 right now. Based on that and the condition, I can offer $4,800 cash today.'"
  },
  {
    id: "electronics-inspection-checklist",
    title: "Electronics Inspection Checklist: Don't Buy a Brick",
    preview: "Buying iPhones, MacBooks, or gaming consoles locally? Follow this checklist to avoid getting scammed.",
    content: "Used electronics are high-target items for scammers. If you are buying a smartphone, laptop, or gaming console on OfferUp or Facebook Marketplace, you must inspect the item thoroughly before handing over the cash.\n\nAlways insist on meeting somewhere with a power outlet and public Wi-Fi, like a coffee shop. For smartphones, specifically check the IMEI number online to ensure it hasn't been reported stolen or blacklisted. You must also verify that the phone is unlocked and signed out of any iCloud or Google accounts. A phone that is iCloud locked is effectively a brick.\n\nFor laptops, run a battery health check, test all the keys on the keyboard, plug a device into every USB port, and ensure the screen has no dead pixels. Finally, beware of 'box seals'. Scammers are now shrink-wrapping empty boxes or fake devices to look brand new. Never buy a sealed box without opening it to verify the contents first."
  },
  {
    id: "the-bundle-and-save-strategy",
    title: "The 'Bundle and Save' Strategy for Facebook Marketplace",
    preview: "Want an easy way to get 20% off without offending the seller? Use the bundle technique.",
    content: "One of the most effective, low-friction negotiation tactics is the 'Bundle and Save' strategy. Sellers on Facebook Marketplace usually want one thing more than a high price: a fast, easy cleanout.\n\nIf you see an item you want for $100, instead of just offering $80, check the seller's other listings. Do they have anything else you might need, or something you could easily resell? If they have a $50 item, send a message saying: 'I am interested in both the desk and the lamp. Since I can pick them both up at the same time and save you a trip, would you take $110 for the pair?'\n\nThis works beautifully because it shifts the framing. You aren't just haggling them down; you are providing value by clearing out multiple items at once. They save time and hassle, and you get a massive discount on the primary item you wanted."
  },
  {
    id: "negotiating-when-seller-is-firm",
    title: "How to Negotiate When the Seller Says 'Firm on Price'",
    preview: "Is 'Firm on Price' really the final word? Not if you know how to leverage time and convenience.",
    content: "You find the perfect item, but the description has those dreaded words: 'Price is Firm. No Lowballs.' Many buyers just walk away, but 'firm' is rarely actually firm—it usually just means 'I am tired of dealing with flakes.'\n\nSellers who post 'Firm' are usually burned out by buyers offering half the asking price and then never showing up. You can break through this barrier by leading with extreme reliability and a fair, data-backed offer.\n\nInstead of starting with a number, start with logistics. 'Hi, I can see you are firm on price, but I wanted to reach out. I am local, I have a truck, and I can be at your house with cash in 30 minutes.' If the item has been sitting for a few days, they might jump at the chance to just get rid of it. If they still demand full price, offer a modest, respectful discount backed by live market comps using Haggler. Often, the relief of a guaranteed, immediate sale will melt a 'firm' price right away."
  },
  {
    id: "offerup-vs-facebook-marketplace",
    title: "OfferUp vs. Facebook Marketplace: Where Are the Best Deals?",
    preview: "Which platform has the best local deals, and which one has the most scammers? A quick breakdown.",
    content: "The local classifieds landscape has completely shifted over the last five years. Craigslist has largely faded, leaving Facebook Marketplace and OfferUp as the two heavyweights. But where should you spend your time hunting for deals?\n\nFacebook Marketplace is currently the undisputed king of volume. Because it's tied to real Facebook profiles, there is a slightly higher level of trust and accountability. It's the best place for furniture, cars, and home goods. However, the search algorithm is notoriously terrible, often showing you items that are states away.\n\nOfferUp is fantastic for smaller items, electronics, and sneakers. Its app is cleaner, and the search function works beautifully. The downside? OfferUp is notorious for 'flakes' (buyers who never show up) and a higher volume of scammers shipping fake items. \n\nThe ultimate strategy is cross-referencing. When you find an item on Facebook, quickly search for it on OfferUp. Sellers often cross-post, and they might have lowered the price on one platform but forgotten to update the other. Use Haggler to quickly verify the true value before messaging on either platform."
  },
  {
    id: "the-power-of-silence",
    title: "The Power of Silence in Local Negotiations",
    preview: "Sometimes the best thing you can say during a negotiation is absolutely nothing.",
    content: "In any negotiation, the person who speaks next loses leverage. Silence is one of the most uncomfortable human experiences, and you can use that discomfort to get better deals on Marketplace.\n\nWhen you are doing an in-person meetup, inspect the item thoroughly. If you find a flaw, point it out calmly. 'I see the screen has a fairly deep scratch here.' Then, stop talking. Do not immediately offer a lower price. Do not ask for a discount. Just look at the item and wait.\n\nThe seller will almost always fill the silence to relieve the tension. Often, they will negotiate against themselves! They might say, 'Yeah, I know about the scratch. I'd be willing to knock $20 off for that.' You just got a discount without even asking.\n\nIf they don't offer a discount, you can break the silence with a data-backed counteroffer from your Haggler Dossier. But always give silence a chance to do the heavy lifting first."
  },
  {
    id: "safely-buying-watches-and-jewelry",
    title: "How to Safely Buy High-End Watches and Jewelry Locally",
    preview: "Buying a Rolex or gold chain locally can save you thousands, but one mistake can cost you everything.",
    content: "The secondary market for luxury watches (Rolex, Omega, Tag Heuer) and fine jewelry is booming on Facebook Marketplace. You can avoid massive dealer markups, but you are also swimming in waters filled with high-level counterfeits.\n\nRule number one: Never, ever buy a luxury watch or expensive piece of jewelry in a parking lot. Super fakes (often called 'franken-watches') are so good today that even experienced buyers can be fooled by the weight and the sweeping second hand.\n\nThe only acceptable way to execute this transaction is meeting at a reputable, local jewelry store or watchmaker. Tell the seller: 'I am highly interested. I will cover the cost of a quick authentication at [Local Jeweler]. If it checks out, I will hand you the cash right there.' \n\nIf the seller makes any excuse—'I don't have time for that,' 'I lost the box but trust me it's real,' or 'I already have another buyer'—walk away immediately. A legitimate seller will gladly meet at a jeweler because it guarantees them a safe, secure location to receive a large amount of cash."
  },
  {
    id: "sneaky-dealership-tactics-craigslist",
    title: "Sneaky Dealership Tactics on Craigslist to Watch Out For",
    preview: "Think you're buying a car from a private seller? Make sure it's not a 'curbstoner' in disguise.",
    content: "You're scrolling Craigslist or Marketplace for a used car and find a great deal from what looks like a normal family. But when you arrive, the 'seller' has four other cars parked on the street and is flipping you the keys to a vehicle not registered in their name.\n\nWelcome to 'Curbstoning'. These are unlicensed, unregulated dealers flipping cheap auction cars for profit, while pretending to be private sellers to avoid lemon laws and taxes.\n\nHow do you spot them? \n1. Search their phone number in the Marketplace search bar. If five different cars pop up, they are a dealer.\n2. When you call or message, say: 'I'm calling about the car.' If they respond, 'Which one?', you are talking to a dealer.\n3. Ask to see the title. If the seller's ID does not match the name printed on the front of the title, walk away. They are title-jumping, which is illegal and will cause you massive headaches at the DMV.\n\nAlways use Haggler to price the car as a standard private party sale, and if you realize you are dealing with a curbstoner, aggressively dock the price for the lack of a proper inspection or warranty."
  },
  {
    id: "best-days-and-times-for-deals",
    title: "The Best Days and Times to Find Steals on Facebook Marketplace",
    preview: "Timing is everything. Learn the optimal schedule to refresh your feed and snipe the best deals before anyone else.",
    content: "The best deals on local marketplaces rarely last more than an hour. If an item is severely underpriced (a 'Steal' on the Haggler rating scale), there are dozens of flippers and savvy buyers ready to pounce. You need to know when to look.\n\nThe absolute best time to refresh Facebook Marketplace is Friday afternoon and Saturday morning. Why? People spend processing their weekends cleaning out their garages, upgrading their living rooms, or preparing for a move. They want the old stuff gone before the weekend is over, leading to rock-bottom prices.\n\nSunday evening is the second best window. If someone hosted a garage sale all weekend and still has inventory left over, they will often dump it on Marketplace for pennies just so they don't have to carry it back inside.\n\nSet up your Haggler Watchlist, track the items you want, and be ready to message sellers immediately when those prime weekend windows hit."
  },
  {
    id: "the-cash-in-hand-advantage",
    title: "The 'Cash in Hand' Advantage and How to Leverage It",
    preview: "The three most powerful words in negotiation are 'Cash in Hand.' Here is exactly how and when to use them.",
    content: "In the world of local classifieds, cash is absolute king. Sellers are terrified of chargebacks, fake Venmo emails, and bounced checks. The promise of crisp, physical bills is the ultimate leverage point.\n\nWhen you find an item you want, never just ask 'Will you take $300?' That sounds hypothetical. Instead, attach the payment method and the timeframe to the offer to make it concrete and irresistible. \n\nMessage them with: 'I see you are asking $400. I can offer $300 cash in hand, and I can pick it up within the hour.' \n\nYou are offering them three massive benefits: immediate money, zero risk of digital fraud, and the instant removal of the item from their house. Sellers will routinely take a 20-30% pay cut on their asking price in exchange for the certainty and speed of a 'cash in hand' offer today. Keep your Haggler scripts ready, pull the live comps, and let the cash do the closing."
  }
];

// --- SEO INJECTION HELPERS ---
const updateMetaTags = (title, description) => {
  document.title = title;
  let metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) {
    metaDesc.content = description;
  }
};

const injectJSONLD = (schemaData) => {
  let script = document.getElementById('seo-jsonld');
  if (!script) {
    script = document.createElement('script');
    script.id = 'seo-jsonld';
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(schemaData);
};

// --- API RETRY LOGIC (WITH QUOTA HANDLING) ---
const fetchWithRetry = async (url, options) => {
  let delay = 1000;
  let lastError;
  
  for (let i = 0; i < 5; i++) {
    try {
      const res = await fetch(url, options);
      if (res.ok) return res;
      
      const errData = await res.json().catch(() => ({}));
      lastError = new Error(errData.error?.message || `API Error ${res.status}`);
      
      // Immediately throw specific client errors
      if (res.status === 400 || res.status === 403 || res.status === 404) {
        throw lastError;
      }
    } catch (e) {
      lastError = e;
      if (e.message.includes("400") || e.message.includes("403") || e.message.includes("404")) {
        throw e;
      }
    }
    
    if (i < 4) {
      await new Promise(r => setTimeout(r, delay));
      delay *= 2;
    }
  }
  
  if (lastError.message.includes("Quota exceeded") || lastError.message.includes("429")) {
    throw new Error("API rate limit reached. Please wait 60 seconds before scanning again, or upgrade your Gemini API key billing.");
  }
  if (lastError.message.includes("high demand") || lastError.message.includes("503")) {
    throw new Error("The AI network is extremely congested right now. We attempted to reconnect but timed out. Please wait 60 seconds and try again.");
  }
  
  throw lastError;
};

// --- UI COMPONENTS ---
const AdSidebar = ({ side }) => (
  <aside className={`hidden 2xl:flex fixed top-0 ${side === 'left' ? 'left-0' : 'right-0'} h-full w-[calc(50vw-340px)] items-center justify-center p-12 z-0 pointer-events-none`}>
    <div className="w-full max-w-[250px] h-[750px] border border-white/5 rounded-[3rem] bg-zinc-900/20 backdrop-blur-md shadow-2xl flex flex-col items-center justify-center p-6 opacity-30">
      <span className="text-[9px] font-black uppercase text-zinc-500/50 [writing-mode:vertical-lr] tracking-[0.8em]">Sponsored Content</span>
    </div>
  </aside>
);

const AdMobileBottom = () => (
  <div className="fixed bottom-32 left-1/2 -translate-x-1/2 w-[90%] max-w-[320px] h-[50px] bg-zinc-900/80 backdrop-blur-xl border border-white/10 rounded-2xl flex items-center justify-center z-[90] shadow-2xl lg:hidden">
    <span className="text-[8px] font-black uppercase text-zinc-500 tracking-[0.3em] italic">Marketplace Intelligence Partner</span>
  </div>
);

const LegalModal = ({ type, onClose }) => (
  <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-2xl flex items-center justify-center p-4 transition-all duration-300">
    <section className="w-full max-w-md bg-zinc-900/90 backdrop-blur-3xl border border-white/10 rounded-[3.5rem] overflow-hidden flex flex-col max-h-[85vh] shadow-2xl text-left animate-in zoom-in-95 duration-300">
      <header className="p-8 border-b border-white/5 flex justify-between items-center bg-black/20">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-orange-500" />
          <h2 className="font-black italic uppercase text-white tracking-widest text-base">{type === 'TOS' ? 'Terms' : 'Privacy'}</h2>
        </div>
        <button onClick={onClose} className="p-3 bg-white/5 rounded-full text-white hover:bg-red-500/20 hover:text-red-500 transition-all" aria-label="Close Modal"><X className="w-5 h-5" /></button>
      </header>
      <article className="p-10 overflow-y-auto text-sm text-zinc-400/90 space-y-8 font-mono text-left no-scrollbar leading-relaxed">
        {type === 'TOS' ? (
          <>
            <h3 className="text-orange-500 font-black underline italic uppercase text-sm">1. Operating Mandate</h3>
            <p>Haggler provides estimates for educational use. Credits are non-refundable once an audit is generated.</p>
          </>
        ) : (
          <>
            <h3 className="text-orange-500 font-black underline italic uppercase text-sm">2. Data Sovereignty</h3>
            <p>Processing occurs via Google Gemini. No raw images are stored permanently on LUNCHBOX servers.</p>
          </>
        )}
      </article>
    </section>
  </div>
);

const TutorialOverlay = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const steps = [
    { icon: Search, title: "Welcome to Haggler", desc: "Stop guessing market values. Haggler uses AI to pull live comps and write your negotiation scripts for any transaction." },
    { icon: Camera, title: "1. Feed the Core", desc: "Upload screenshots of listings, price sheets, descriptions, and History Reports (e.g., Zillow, CARFAX)." },
    { icon: TrendingUp, title: "2. Set Your Strategy", desc: "Select your deal sector (Auto, Real Estate, General, Luxury) and your strategy (Buy or Resell)." },
    { icon: FileText, title: "3. Close the Deal", desc: "Copy the generated negotiation scripts or send the Haggler Report directly to the seller as proof." }
  ];

  const CurrentIcon = steps[step].icon;

  return (
    <div className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-2xl flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-500">
      <section className="w-full max-w-sm bg-zinc-900/80 backdrop-blur-xl border border-white/10 rounded-[3rem] p-10 shadow-2xl relative overflow-hidden flex flex-col items-center min-h-[450px] justify-between">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-600 to-amber-500" />
        
        <div className="flex-1 flex flex-col items-center justify-center w-full animate-in zoom-in-95 duration-500" key={step}>
            <div className="w-24 h-24 bg-black/50 border border-white/5 rounded-[2.5rem] flex items-center justify-center mb-8 shadow-inner">
              <CurrentIcon className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-2xl font-black italic uppercase text-white tracking-tight mb-4">{steps[step].title}</h2>
            <p className="text-sm font-medium text-zinc-400/90 leading-relaxed">{steps[step].desc}</p>
        </div>

        <div className="w-full mt-8 flex flex-col gap-6">
            <div className="flex justify-center gap-2" aria-hidden="true">
              {steps.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i === step ? 'w-8 bg-orange-500 shadow-[0_0_10px_rgba(234,88,12,0.5)]' : 'w-2 bg-zinc-800'}`} />
              ))}
            </div>
            <button 
              onClick={() => {
                if (step < steps.length - 1) setStep(step + 1);
                else onComplete();
              }} 
              className="w-full h-16 bg-orange-600 rounded-[2rem] font-black uppercase text-xs tracking-[0.3em] text-white shadow-xl hover:bg-orange-500 hover:scale-[1.02] active:scale-95 transition-all duration-300 flex items-center justify-center gap-2"
            >
              {step < steps.length - 1 ? 'Next' : 'Start Haggling!'}
              {step < steps.length - 1 ? <ChevronRight className="w-4 h-4" /> : <Search className="w-4 h-4" />}
            </button>
        </div>
      </section>
    </div>
  );
};

// --- MAIN APPLICATION ---
const App = () => {
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(0);
  const [watchlist, setWatchlist] = useState([]);
  const [publicFeed, setPublicFeed] = useState([]);
  const [adminReviews, setAdminReviews] = useState([]); 
  
  const [usedPromoCodes, setUsedPromoCodes] = useState([]);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showLangModal, setShowLangModal] = useState(false);
  
  // DYNAMIC GOD MODE DERIVED STATE
  const isGodMode = credits > 9000 || (typeof localStorage !== 'undefined' && localStorage.getItem('haggler_god_mode') === 'true');

  const [view, setView] = useState(() => {
    if (typeof window !== 'undefined') {
      const url = window.location.href.toLowerCase();
      const path = window.location.pathname.toLowerCase();
      if (url.includes('success') || url.includes('session_id')) return 'SUCCESS';
      if (path.includes('/verify/')) return 'VERIFY';
      if (path.includes('vault')) return 'WATCHLIST';
      if (path.includes('feed')) return 'FEED';
      if (path.includes('refuel')) return 'PRICING';
      if (path.includes('academy')) return 'ACADEMY';
      if (path.includes('rewards')) return 'REWARDS';
    }
    return 'SCAN';
  }); 

  const [verifyAuditId, setVerifyAuditId] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname.toLowerCase().includes('/verify/')) {
        const parts = window.location.pathname.split('/verify/');
        if (parts.length > 1) {
            return parts[1].replace(/[^a-zA-Z0-9-]/g, '').toUpperCase();
        }
    }
    return null;
  });
  const [verifyData, setVerifyData] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(true);

  const [activeArticle, setActiveArticle] = useState(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.toLowerCase();
      if (path.includes('/academy/')) {
        return path.split('/academy/')[1].replace(/[^a-z0-9-]/g, '');
      }
    }
    return null;
  });
  
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]); 
  const [imagePreviews, setImagePreviews] = useState([]); 
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [tactic, setTactic] = useState('medium');
  const [copied, setCopied] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  
  const [buyingStrategy, setBuyingStrategy] = useState('buy'); 
  const [dealSector, setDealSector] = useState('general');
  const [language, setLanguage] = useState('English');
  const [showDossier, setShowDossier] = useState(false);
  const [flawsExpanded, setFlawsExpanded] = useState(false);
  const [counterInput, setCounterInput] = useState('');
  const [counterLoading, setCounterLoading] = useState(false);
  const [counterScript, setCounterScript] = useState(null);

  const [promoInput, setPromoInput] = useState('');
  const [promoMessage, setPromoMessage] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const [checkoutMsg, setCheckoutMsg] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPass, setAdminPass] = useState('');
  const [sysStatus, setSysStatus] = useState('INITIALIZING...');
  const [acceptedTerms, setAcceptedTerms] = useState(() => localStorage.getItem('haggler_terms_accepted') === 'true');
  const [agreedToClickwrap, setAgreedToClickwrap] = useState(false);
  const [showLegal, setShowLegal] = useState(null);
  const [showTutorial, setShowTutorial] = useState(() => localStorage.getItem('haggler_tutorial_seen') !== 'true');

  const hasFulfilled = useRef(false);

  // --- UI LOCALIZATION HELPER ---
  const t = (key) => UI_TEXT[language] ? UI_TEXT[language][key] : UI_TEXT['English'][key];

  const [scannerText, setScannerText] = useState('');

  // --- DYNAMIC THEME SELECTOR ---
  const getTheme = () => {
    switch(dealSector) {
      case 'auto': return { text: 'text-blue-500', bg: 'bg-blue-600', border: 'border-blue-500/50', glow: 'rgba(59,130,246,0.4)', lightBg: 'bg-blue-600/10' };
      case 'real_estate': return { text: 'text-emerald-500', bg: 'bg-emerald-600', border: 'border-emerald-500/50', glow: 'rgba(16,185,129,0.4)', lightBg: 'bg-emerald-600/10' };
      case 'luxury': return { text: 'text-amber-400', bg: 'bg-amber-500', border: 'border-amber-400/50', glow: 'rgba(251,191,36,0.4)', lightBg: 'bg-amber-500/10' };
      default: return { text: 'text-orange-500', bg: 'bg-orange-600', border: 'border-orange-500/50', glow: 'rgba(234,88,12,0.4)', lightBg: 'bg-orange-600/10' };
    }
  };
  const theme = getTheme();

  // --- GLOBAL PASTE LISTENER ---
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (view === 'SCAN' && !loading && !analysis && !showDossier && !showProfileModal && !showLangModal) {
        if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
          e.preventDefault();
          processFiles(e.clipboardData.files);
        }
      }
    };
    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [view, loading, analysis, showDossier, showProfileModal, showLangModal]);

  // --- DYNAMIC SEO ENGINE ---
  useEffect(() => {
    let title = "Haggler | AI Negotiation Engine & Deal Scanner";
    let desc = "Haggler is the ultimate AI negotiator. Instantly analyze and win deals on Real Estate, Cars, Electronics, and Marketplace Listings. Let Haggler find true market value.";
    let schema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Haggler",
      "alternateName": ["Haggler AI", "Haggler USA", "The Haggler App"],
      "url": "https://hagglerusa.com",
      "applicationCategory": "BusinessApplication",
      "description": "Haggler AI Negotiation Engine",
      "offers": { "@type": "Offer", "price": "0.00", "priceCurrency": "USD" }
    };

    if (view === 'ACADEMY') {
      if (activeArticle) {
        const article = ACADEMY_ARTICLES.find(a => a.id === activeArticle);
        if (article) {
          title = `${article.title} | Haggler Academy`;
          desc = article.preview;
          schema = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.preview,
            "author": { "@type": "Organization", "name": "Haggler" }
          };
        }
      } else {
        title = "Haggler Academy | Master the Deal with Haggler";
        desc = "Learn advanced negotiation tactics with Haggler. How to spot scammers, and the best ways to win deals using the Haggler AI engine.";
        schema = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": ACADEMY_ARTICLES.map(a => ({
            "@type": "Question",
            "name": a.title,
            "acceptedAnswer": { "@type": "Answer", "text": a.preview }
          }))
        };
      }
    } else if (view === 'PRICING') {
      title = "Haggler Pricing & Refuel | Haggler AI";
    } else if (view === 'REWARDS') {
      title = "Earn Free Scans | Haggler Rewards";
    } else if (view === 'WATCHLIST') {
      title = "My Market Vault | Haggler";
    } else if (view === 'FEED') {
      title = "Live Market Scans | Haggler";
    }

    updateMetaTags(title, desc);
    injectJSONLD(schema);
  }, [view, activeArticle]);

  useEffect(() => {
    if (!auth) return;
    const initAuth = async () => {
      try {
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          await signInWithCustomToken(auth, __initial_auth_token);
        } else {
          await signInAnonymously(auth);
        }
      } catch (e) { console.error("Auth Error", e); }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !db) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');

    const profRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
    const unsubProf = onSnapshot(profRef, (s) => {
      if (s.exists()) {
        const data = s.data();
        setCredits(typeof data.credits === 'number' ? data.credits : 0);
        setUsedPromoCodes(data.usedPromoCodes || []);
        setHasReviewed(data.hasReviewed || false);
        setSysStatus('LIVE');
      } else {
        const isSuccessRoute = window.location.href.toLowerCase().includes('success') || window.location.href.toLowerCase().includes('session_id');
        if (!isSuccessRoute) {
          let startingCredits = refCode ? 8 : 3;
          setDoc(profRef, { 
            credits: startingCredits, 
            createdAt: serverTimestamp(), 
            tier: 'free', 
            userId: user.uid,
            referredBy: refCode || null,
            usedPromoCodes: [],
            hasReviewed: false
          });
        }
      }
    }, () => setSysStatus('SYNC_ERROR'));

    const wlCol = collection(db, 'artifacts', appId, 'users', user.uid, 'watchlist');
    const unsubWl = onSnapshot(query(wlCol), (snap) => {
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setWatchlist(items.sort((a,b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)));
    });

    // Public Feed Fetch
    const pubCol = collection(db, 'artifacts', appId, 'public', 'data', 'reports');
    const unsubPub = onSnapshot(query(pubCol, orderBy('timestamp', 'desc'), limit(50)), (snap) => {
      setPublicFeed(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubProf(); unsubWl(); unsubPub(); };
  }, [user]);

  // VERIFICATION FETCHER
  useEffect(() => {
    if (view === 'VERIFY' && verifyAuditId && db && user) {
      setVerifyLoading(true);
      const fetchReport = async () => {
        try {
          const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'reports', verifyAuditId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setVerifyData(docSnap.data());
          } else {
            setVerifyData(false);
          }
        } catch (e) {
          console.error("Verification fetch error", e);
          setVerifyData(false);
        } finally {
          setVerifyLoading(false);
        }
      };
      fetchReport();
    }
  }, [view, verifyAuditId, db, user]);

  // ADMIN DASHBOARD DATA FETCHER
  useEffect(() => {
    if (view === 'ADMIN' && db) {
      const unsub = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'reviews'), (snap) => {
        const revs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAdminReviews(revs.sort((a,b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)));
      });
      return () => unsub();
    }
  }, [view, db]);

  useEffect(() => {
    const handleSuccessRedirect = async () => {
      if (view === 'SUCCESS' && user && db && !hasFulfilled.current) {
        hasFulfilled.current = true; 
        const pendingTier = localStorage.getItem('pending_haggler_tier');
        const urlParams = new URLSearchParams(window.location.search);
        const purchasedTier = urlParams.get('tier') || pendingTier;
        
        if (purchasedTier) {
          let creditsToAdd = 0;
          const t = purchasedTier.toUpperCase();
          if (t === 'STARTER') creditsToAdd = 10;
          else if (t === 'PRO') creditsToAdd = 25;
          else if (t === 'ELITE') creditsToAdd = 60;

          if (creditsToAdd > 0) {
            try {
              const profRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
              const profSnap = await getDoc(profRef);
              if (profSnap.exists()) {
                await updateDoc(profRef, { credits: increment(creditsToAdd), tier: t.toLowerCase() });
              } else {
                await setDoc(profRef, { credits: creditsToAdd + 3, tier: t.toLowerCase(), createdAt: serverTimestamp(), userId: user.uid });
              }
              localStorage.removeItem('pending_haggler_tier');
              window.history.replaceState({}, document.title, window.location.pathname);
            } catch (err) { console.error("Credit Fulfillment Error", err); }
          }
        }
      }
    };
    handleSuccessRedirect();
  }, [view, user, db]);

  // Hollywood Scanner Typewriter Effect
  useEffect(() => {
    if (!loading) return;
    const texts = [t('scannerExtracting'), t('scannerRunning'), t('scannerIsolating'), t('scannerCross'), t('scannerCalc')];
    let i = 0;
    setScannerText(texts[0]);
    const interval = setInterval(() => {
      i = (i + 1) % texts.length;
      setScannerText(texts[i]);
    }, 1500);
    return () => clearInterval(interval);
  }, [loading, language]);

  // Haptic Feedback Trigger
  useEffect(() => {
    if (analysis && (analysis.dealRating === 'SCAM' || analysis.dealRating === 'STEAL')) {
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate([200, 100, 200]);
    }
  }, [analysis]);

  const handleCopyText = (text, type = "Script") => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setCheckoutMsg(`${type.toUpperCase()} COPIED`);
    setTimeout(() => { setCopied(false); setCheckoutMsg(false); }, 2000);
  };

  const handleShareDossier = async () => {
    if (!analysis) return;
    setCheckoutMsg("GENERATING HAGGLER REPORT...");
    try {
      if (!window.html2canvas) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      const element = document.getElementById('dossier-capture-area');
      const canvas = await window.html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff', allowTaint: true });

      canvas.toBlob(async (blob) => {
        if (!blob) throw new Error("Canvas to Blob failed");
        const safeName = analysis.assetName ? analysis.assetName : 'Item';
        const fileName = `Haggler_Report_${safeName.replace(/[^a-z0-9]/gi, '_')}.png`;
        const file = new File([blob], fileName, { type: 'image/png' });
        
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            setCheckoutMsg(false);
            await navigator.share({ title: 'Haggler Report', files: [file] });
          } catch (err) { console.log('Share canceled or failed', err); }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(url);
          setCheckoutMsg("IMAGE DOWNLOADED");
          setTimeout(() => setCheckoutMsg(false), 3000);
        }
      }, 'image/png');
    } catch (err) {
      console.error('Image Generation Failed', err);
      setCheckoutMsg("FAILED TO GENERATE IMAGE");
      setTimeout(() => setCheckoutMsg(false), 3000);
    }
  };

  const handleCopyDossier = async () => {
    if (!analysis) return;
    setCheckoutMsg("COPYING HAGGLER REPORT...");
    try {
      if (!window.html2canvas) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      const element = document.getElementById('dossier-capture-area');
      const canvas = await window.html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff', allowTaint: true });

      canvas.toBlob(async (blob) => {
        if (!blob) throw new Error("Canvas to Blob failed");
        try {
          const item = new ClipboardItem({ "image/png": blob });
          await navigator.clipboard.write([item]);
          setCheckoutMsg("IMAGE COPIED TO CLIPBOARD");
          setTimeout(() => setCheckoutMsg(false), 3000);
        } catch (err) {
          console.error("Clipboard write failed", err);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Haggler_Report_${analysis.auditId}.png`;
          a.click();
          URL.revokeObjectURL(url);
          setCheckoutMsg("COULD NOT COPY. DOWNLOADING INSTEAD.");
          setTimeout(() => setCheckoutMsg(false), 3000);
        }
      }, 'image/png');
    } catch (err) {
      console.error('Image Copy Failed', err);
      setCheckoutMsg("FAILED TO COPY IMAGE");
      setTimeout(() => setCheckoutMsg(false), 3000);
    }
  };

  const handleCloseExpanded = () => {
    setExpandedImage(null);
    setIsZoomed(false);
  };

  const navigateTo = (e, targetView, path) => {
    if (e) e.preventDefault();
    setAnalysis(null); 
    setImagePreviews([]); 
    setImages([]); 
    setError(null); 
    setFlawsExpanded(false); 
    setActiveArticle(null); 
    setView(targetView); 
    window.history.pushState({}, '', path); 
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const handleArticleClick = (articleId) => {
    setActiveArticle(articleId);
    window.history.pushState({}, '', `/academy/${articleId}`);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      setCheckoutMsg("SIGNED IN SUCCESSFULLY");
      setTimeout(() => setCheckoutMsg(false), 3000);
      setShowProfileModal(false);
    } catch (error) {
      console.error("Sign in error:", error);
      setCheckoutMsg("SIGN IN FAILED");
      setTimeout(() => setCheckoutMsg(false), 3000);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      await signInAnonymously(auth); 
      setCheckoutMsg("SIGNED OUT");
      setTimeout(() => setCheckoutMsg(false), 3000);
      setShowProfileModal(false);
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const updateFinancials = async (itemId, field, value) => {
    if (!db || !user) return;
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'watchlist', itemId), { [field]: Number(value) || 0 });
    } catch (e) { console.error("Update failed", e); }
  };

  const performScan = async () => {
    if (!isGodMode && credits <= 0) { setView('PRICING'); return; }
    if (images.length === 0) { setError("Please upload screenshots of the listing, including the description and asking price."); return; }
    
    setLoading(true); setError(null); setAnalysis(null); setCounterScript(null); setCounterInput(''); setFlawsExpanded(false);

    try {
      const strategyPrompt = buyingStrategy === 'sell' 
        ? "BUYING TO RESELL FOR PROFIT: You MUST calculate a 30% profit margin based on the asking price. Subtract this margin AND all repair/market deductions from the Asking Price to get the final target valuation." 
        : "BUYING FOR PERSONAL USE: Subtract all repair/market deductions from the Asking Price to get the final target valuation. Margin is 0.";

      let sectorDirectives = "";
      if (dealSector === 'auto') {
         sectorDirectives = "CRITICAL DIRECTIVE 9 (SECTOR: AUTOMOTIVE): Focus heavily on identifying mechanical flaws, dealership documentation fees (if applicable), aftermarket inflation, mileage, and vehicle history (Carfax/AutoCheck). Deductions MUST reflect realistic automotive repair costs (e.g., timing belt, transmission, tires, paint correction).";
      } else if (dealSector === 'real_estate') {
         sectorDirectives = "CRITICAL DIRECTIVE 9 (SECTOR: REAL ESTATE): Focus on structural integrity, roof age, foundation, neighborhood comps, and days on market based on the Zillow/MLS listing. DO NOT use trivial deductions like 'dust' or 'cleaning'. Deductions MUST reflect contractor-level repair estimates. NEVER value a livable house at $0 or negative. Use Google Search for local real estate comps.";
      } else if (dealSector === 'luxury') {
         sectorDirectives = "CRITICAL DIRECTIVE 9 (SECTOR: LUXURY, JEWELRY & COLLECTIBLES): Focus on authenticity, provenance, and specialty market comps. FOR JEWELRY/WATCH LOTS: If multiple items are detected, identify each item and its likely material composition (e.g., 18k gold, sterling silver, stainless steel, base metal). Estimate the precious metal weight/content. You MUST use Google Search to pull today's live spot prices for precious metals to calculate the absolute 'melt/scrap' floor value of the lot. Use this intrinsic material value to aggressively haggle down the asking price if the seller is overvaluing scrap or base-metal items. Base your valuation on intrinsic melt/stone value vs. brand premium, and cite these live commodity price sources.";
      } else {
         sectorDirectives = "CRITICAL DIRECTIVE 9 (SECTOR: GENERAL GOODS): Focus on functional condition, missing accessories, and standard wear-and-tear for consumer electronics, furniture, or tools. Proportionate deductions are key; do not over-penalize cheap items.";
      }

      const systemPrompt = `You are Haggler, an expert negotiator for the BUYER. 
      CRITICAL DIRECTIVE 1 (GROUND TRUTH): Extract the precise item/property name into 'assetName' (Do NOT use the word 'Haggler'). If multiple items are detected, summarize them (e.g., "Vintage Watch Lot"). Extract the exact Asking Price. IF NO ASKING PRICE IS VISIBLE IN THE IMAGES, DO NOT hallucinate one. Set 'askingPriceNumeric' to 0 and 'askingPriceFormatted' to "Not Provided".
      CRITICAL DIRECTIVE 2 (ANTI-HALLUCINATION & DEDUCTIONS): If NO asking price is provided, DO NOT assign dollar amounts to itemized flaws. Set their 'deductionNumeric' to 0 and 'deductionFormatted' to "TBD (Needs Price)". Explicitly state "Not enough info given" in the dealAnalysis.
      CRITICAL DIRECTIVE 3 (PROPORTIONATE DEDUCTIONS): If an asking price IS provided, your deductions MUST reflect the real-world value of the item. DO NOT invent absurd deductions for cheap goods.
      CRITICAL DIRECTIVE 4 (MAXIMUM DEDUCTION CAP): Do NOT negotiate an item down to $0 unless it is literally destroyed. Total deductions should rarely exceed 40-50% of the asking price for working/livable items.
      CRITICAL DIRECTIVE 5 (STRATEGY): ${strategyPrompt}
      CRITICAL DIRECTIVE 6 (PERFECT MATH): If an asking price is provided, finalValuationNumeric MUST equal: (askingPriceNumeric - totalDeductionsNumeric - flipperMarginNumeric). If NO asking price is provided, finalValuationNumeric MUST equal your estimated true market value based on live comps minus necessary repairs, and totalDeductionsNumeric must be 0.
      CRITICAL DIRECTIVE 7 (LIVE COMPS): Use the Google Search tool to find real part/comp URLs and put them in the 'sources' array.
      CRITICAL DIRECTIVE 8 (DOCUMENT INGESTION): If the user uploads a History Report or Inspection Sheet, actively analyze it. Extract specific inspector callouts as explicit dollar deductions.
      ${sectorDirectives}
      CRITICAL DIRECTIVE 10 (LANGUAGE): The generated negotiation scripts MUST be written natively in ${language}.
      CRITICAL DIRECTIVE 11 (REPORT TRANSLATION): All flaw descriptions, deal analysis, and string values in the JSON output MUST be strictly translated into ${language} (e.g. translate "TBD (Needs Price)" to ${language} if needed).
      
      Instructions:
      1. Extract 'askingPriceNumeric' as a raw integer or 0 if missing.
      2. Grade the deal (STEAL, GOOD, FAIR, POOR, SCAM, or UNKNOWN if no price is provided).
      3. Identify REALISTIC flaws/market conditions that make sense for the item's price tier. Add to 'itemizedFlaws' with a reasonable 'deductionNumeric' (or 0 if no price).
      4. Calculate 'totalDeductionsNumeric'.
      5. Calculate 'flipperMarginNumeric' (30% of asking if resell, else 0).
      6. Calculate 'finalValuationNumeric'.
      7. Format currency strings. If asking price is missing, askingPriceFormatted should be "Not Provided".
      8. Provide 'sources' array with title and url.
      9. Create Soft, Medium, Hard negotiation scripts. If listing info is missing, write scripts asking for those details before making the offer.
      
      CRITICAL INSTRUCTION: You MUST return your response ONLY as a raw, valid JSON object matching EXACTLY this structure:
      {
        "assetName": "string",
        "askingPriceFormatted": "string",
        "askingPriceNumeric": 10000,
        "dealRating": "STEAL|GOOD|FAIR|POOR|SCAM|UNKNOWN",
        "dealAnalysis": "string",
        "itemizedFlaws": [{"flaw": "string", "deductionFormatted": "-$20", "deductionNumeric": 20}],
        "totalDeductionsNumeric": 20,
        "flipperMarginNumeric": 0,
        "finalValuationNumeric": 9980,
        "finalValuationFormatted": "$9,800",
        "redFlags": ["string"],
        "scripts": { "soft": "string", "medium": "string", "hard": "string" },
        "sources": [{"title": "string", "url": "string"}]
      }`;

      const payload = {
        contents: [{ role: "user", parts: [{ text: systemPrompt }, ...images.map(img => ({ inlineData: { mimeType: img.mimeType, data: img.data } }))] }],
        tools: [{ google_search: {} }],
        generationConfig: {
          temperature: 0.1 
        }
      };
      
      const isPreviewEnvironment = typeof __app_id !== 'undefined';
      let liveUrl;

      if (isPreviewEnvironment) {
        liveUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`;
      } else {
        const modelsRes = await fetchWithRetry(`https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`);
        const modelsData = await modelsRes.json();
        const availableModels = (modelsData.models || []).filter(m => m.supportedGenerationMethods?.includes("generateContent") && !m.name.includes("vision"));
        const bestModel = availableModels.find(m => m.name.includes("1.5-flash")) || availableModels[0];
        liveUrl = `https://generativelanguage.googleapis.com/v1beta/${bestModel.name}:generateContent?key=${apiKey}`;
      }

      const response = await fetchWithRetry(liveUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const result = await response.json();
      const aiText = result.candidates?.[0]?.content?.parts?.[0]?.text;
      
      if (!aiText) throw new Error("The AI returned an empty response. Please try clearer images.");
      
      let parsed;
      try {
        const cleanText = aiText.replace(/[\`]{3}json/gi, '').replace(/[\`]{3}/g, '').trim();
        parsed = JSON.parse(cleanText);
        parsed.auditId = 'HGLR-' + Math.random().toString(36).substring(2, 10).toUpperCase();
        parsed.auditDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        
        // --- ADD THUMBNAIL COMPRESSION FOR VAULT AND FEED ---
        if (imagePreviews && imagePreviews.length > 0) {
           parsed.thumbnail = await generateThumbnail(imagePreviews[0]);
        }
      } catch (parseError) {
        console.error("JSON Parse Error. Raw AI Output:", aiText);
        throw new Error("The AI generated a formatting error. Please try running the audit again.");
      }
      
      if (user && db) {
        await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'watchlist'), { ...parsed, timestamp: serverTimestamp() });
        
        if (!isGodMode) {
            await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data'), { credits: increment(-1) });
        }
        
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'reports', parsed.auditId), {
            ...parsed,
            timestamp: serverTimestamp(),
            creatorId: user.uid
        });
      }
      setAnalysis(parsed);
    } catch (err) { setError(`Audit Failed: ${err.message}`); } 
    finally { setLoading(false); }
  };

  const performCounter = async () => {
    if (!counterInput.trim() || !analysis) return;
    setCounterLoading(true);
    try {
      const payload = {
        contents: [{ 
          role: "user", 
          parts: [{ text: `You are Haggler. Here is our negotiation so far:\nItem: ${analysis.assetName || analysis.model}\nTarget: ${analysis.finalValuationFormatted}\nMy last offer was rejected. The seller just replied: "${counterInput}".\nWrite a strong, single-paragraph counter-response natively in ${language} to send back to the seller to keep the leverage and get the price down. RETURN ONLY RAW JSON: {"counterScript": "string"}` }] 
        }],
        generationConfig: {
          temperature: 0.4 
        }
      };
      
      const modelsRes = await fetchWithRetry(`https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`);
      const availableModels = (await modelsRes.json()).models.filter(m => m.supportedGenerationMethods?.includes("generateContent") && !m.name.includes("vision"));
      const bestModel = availableModels.find(m => m.name.includes("1.5-flash")) || availableModels[0];
      const liveUrl = `https://generativelanguage.googleapis.com/v1beta/${bestModel.name}:generateContent?key=${apiKey}`;

      const response = await fetchWithRetry(liveUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const result = await response.json();
      const aiText = result.candidates?.[0]?.content?.parts?.[0]?.text;
      
      let parsed;
      try {
        const cleanText = aiText.replace(/[\`]{3}json/gi, '').replace(/[\`]{3}/g, '').trim();
        parsed = JSON.parse(cleanText);
      } catch (e) {
        console.error("Counter JSON Parse Error", aiText);
        throw new Error("Failed to format counter response.");
      }
      
      setCounterScript(parsed.counterScript);
      setCounterInput('');
    } catch (err) { console.error("Counter Failed", err); } 
    finally { setCounterLoading(false); }
  };

  const processFiles = (files) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(pv => [...pv, reader.result]);
        setImages(im => [...im, { mimeType: file.type || "image/png", data: reader.result.split(',')[1] }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleAdminTap = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTapCount(prev => {
      const newCount = prev + 1;
      if (newCount >= 10) { 
         setShowAdminLogin(true);
         return 0;
      }
      return newCount;
    });
  };

  const handleAdminSubmit = async () => {
    if (adminPass === 'Snagglenast1es' && user && db) {
      localStorage.setItem('haggler_god_mode', 'true');
      await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data'), { credits: 99999, tier: 'enterprise' }, { merge: true });
      
      setCheckoutMsg("GOD MODE INITIATED: INFINITE SCANS ACTIVE");
      setTimeout(() => setCheckoutMsg(false), 4000);
      setView('ADMIN'); 
    } else {
      setCheckoutMsg("ACCESS DENIED");
      setTimeout(() => setCheckoutMsg(false), 3000);
    }
    setShowAdminLogin(false); setAdminPass('');
  };

  const handlePurchase = (tier) => {
    if (!user) return;
    let link = STRIPE_LINKS[tier];
    if (link) {
      localStorage.setItem('pending_haggler_tier', tier);
      window.location.href = `${link.trim().replace(/^[^h]*http/i, 'http')}${link.includes('?') ? '&' : '?'}client_reference_id=${user.uid}`;
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoInput || !user || !db) return;
    const code = promoInput.toUpperCase().trim();
    
    if (usedPromoCodes.includes(code)) {
      setPromoMessage({ text: "You have already used this code.", type: "error" });
      setTimeout(() => setPromoMessage(null), 3000);
      return;
    }

    if (CREATOR_CODES[code]) {
      try {
        const reward = CREATOR_CODES[code];
        const profRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
        await updateDoc(profRef, {
          credits: increment(reward),
          usedPromoCodes: [...usedPromoCodes, code]
        });
        setPromoInput('');
        setPromoMessage({ text: `Success! +${reward} Credits Added.`, type: "success" });
        setTimeout(() => setPromoMessage(null), 4000);
      } catch (e) {
        setPromoMessage({ text: "Network error. Try again.", type: "error" });
      }
    } else {
      setPromoMessage({ text: "Invalid or expired code.", type: "error" });
      setTimeout(() => setPromoMessage(null), 3000);
    }
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0 || !user || !db || hasReviewed) return;
    setReviewSubmitted(true);
    
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'reviews'), {
        userId: user.uid,
        rating: reviewRating,
        text: reviewText,
        timestamp: serverTimestamp()
      });

      const profRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
      await updateDoc(profRef, { credits: increment(3), hasReviewed: true });

      setCheckoutMsg("REVIEW SUBMITTED: +3 CREDITS");
      setTimeout(() => setCheckoutMsg(false), 4000);
    } catch (e) {
      console.error(e);
      setReviewSubmitted(false);
    }
  };

  if (!acceptedTerms) {
    return (
      <main className="min-h-screen bg-[#050505] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900/40 via-[#050505] to-[#050505] text-zinc-100 flex items-center justify-center p-6 text-center">
        <section className="w-full max-w-md lg:max-w-lg bg-zinc-900/60 backdrop-blur-2xl border border-white/10 p-12 rounded-[4rem] shadow-[0_20px_60px_rgba(0,0,0,0.8)] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-600 to-amber-500 opacity-80" />
          <div onClick={handleAdminTap} className="w-24 h-24 bg-orange-600 rounded-[2.5rem] flex items-center justify-center mb-10 text-white font-black italic text-6xl mx-auto cursor-pointer shadow-[0_0_40px_rgba(234,88,12,0.4)] select-none hover:scale-105 transition-transform duration-500">H</div>
          <h1 className="text-4xl font-black italic uppercase mb-4 text-white">Welcome to <span className="text-orange-500">Haggler</span></h1>
          <div className="space-y-4 mb-10">
             <button onClick={() => setShowLegal('TOS')} className="w-full bg-zinc-800/50 backdrop-blur border border-white/5 p-6 rounded-[2rem] text-xs font-black uppercase flex justify-between items-center group hover:bg-zinc-800/80 active:scale-95 transition-all duration-300 shadow-sm">Review Terms <ChevronRight className="w-4 h-4 text-orange-500 group-hover:translate-x-1 transition-transform" /></button>
          </div>
          <div className="flex items-start gap-5 mb-10 bg-black/40 backdrop-blur-md p-8 rounded-[2.5rem] border border-white/5 text-left shadow-inner">
            <input type="checkbox" id="cw" checked={agreedToClickwrap} onChange={(e) => setAgreedToClickwrap(e.target.checked)} className="mt-1.5 w-6 h-6 rounded-lg accent-orange-600 cursor-pointer transition-all duration-300 hover:scale-110"/>
            <label htmlFor="cw" className="text-xs text-zinc-300/90 font-medium leading-relaxed cursor-pointer select-none">I verify that I have read and agree to the <span className="text-orange-500 underline hover:text-orange-400 transition-colors">Terms</span> and the <span className="text-orange-500 underline hover:text-orange-400 transition-colors">Refund Policy</span>.</label>
          </div>
          <button onClick={() => { if (!agreedToClickwrap || !user) return; localStorage.setItem('haggler_terms_accepted', 'true'); setAcceptedTerms(true); }} disabled={!agreedToClickwrap || !user} className={`w-full h-24 rounded-[2.5rem] font-black uppercase text-base tracking-[0.3em] transition-all duration-300 ${agreedToClickwrap && user ? 'bg-orange-600 text-white hover:bg-orange-500 shadow-[0_10px_30px_rgba(234,88,12,0.4)] hover:shadow-[0_15px_40px_rgba(234,88,12,0.6)] hover:-translate-y-1' : 'bg-zinc-800/50 text-zinc-600 opacity-50 cursor-not-allowed border border-white/5'}`}>Start Haggling!</button>
        </section>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900/30 via-[#050505] to-[#050505] text-zinc-100 font-sans selection:bg-orange-500/30 overflow-x-hidden antialiased">
      
      {acceptedTerms && showTutorial && (
        <TutorialOverlay onComplete={() => {
          localStorage.setItem('haggler_tutorial_seen', 'true');
          setShowTutorial(false);
        }} />
      )}

      <AdSidebar side="left" />
      <AdSidebar side="right" />

      <div className="w-full max-w-md lg:max-w-2xl mx-auto min-h-screen flex flex-col border-x border-white/5 bg-transparent relative z-10 shadow-[0_0_50px_rgba(0,0,0,0.5)]">
        <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-2xl border-b border-white/5 px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between shadow-2xl">
          <div className="flex items-center gap-4 cursor-pointer group" onClick={(e) => navigateTo(e, 'SCAN', '/')}>
            <div onClick={handleAdminTap} className={`w-10 h-10 ${theme.bg} rounded-2xl flex items-center justify-center font-black italic text-2xl shadow-[0_0_20px_${theme.glow}] select-none text-white transition-all duration-500 group-hover:scale-105`}>H</div>
            <h1 className="font-black italic uppercase text-3xl tracking-tighter text-white transition-opacity hidden sm:block drop-shadow-md">Haggler</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => setShowLangModal(true)} className="flex items-center justify-center gap-1.5 px-4 h-10 bg-zinc-900/80 backdrop-blur-md rounded-full border border-white/10 hover:border-white/30 transition-all duration-300 hover:bg-zinc-800 active:scale-95 shadow-sm" aria-label="Select Language">
              <Languages className="w-4 h-4 text-zinc-300" />
              <span className="text-xs font-black text-white uppercase">{language === 'English' ? 'EN' : language === 'Spanish' ? 'ES' : language === 'French' ? 'FR' : 'ZH'}</span>
            </button>
            <button onClick={() => setShowProfileModal(true)} className="w-10 h-10 bg-zinc-900/80 backdrop-blur-md rounded-full flex items-center justify-center border border-white/10 hover:border-white/30 transition-all duration-300 hover:bg-zinc-800 active:scale-95 overflow-hidden shadow-sm" aria-label="Account Profile">
               {user && !user.isAnonymous && user.photoURL ? (
                 <img src={user.photoURL} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
               ) : (
                 <User className="w-4 h-4 text-zinc-300" />
               )}
            </button>
            <a href="/refuel" onClick={(e) => navigateTo(e, 'PRICING', '/refuel')} className={`bg-zinc-900/80 backdrop-blur-md px-4 sm:px-5 h-10 rounded-full flex items-center justify-center gap-2 sm:gap-2.5 border border-white/10 cursor-pointer active:scale-90 transition-all duration-300 hover:bg-zinc-800 hover:border-white/30 shadow-sm`}>
              <Zap className={`w-4 h-4 ${(isGodMode || credits > 0) ? `${theme.text} drop-shadow-[0_0_8px_${theme.glow}] fill-current` : 'text-zinc-600'}`} />
              <span className="text-sm font-black text-white">{isGodMode ? '∞' : credits}</span>
            </a>
          </div>
        </header>

        <main className="flex-1 flex flex-col p-6 overflow-y-auto no-scrollbar pb-36 text-left relative">
          
          {/* --- ADMIN DASHBOARD --- */}
          {view === 'ADMIN' && (
            <div className="space-y-8 animate-in fade-in duration-500 pb-20">
              <div className="text-center">
                <h2 className="text-5xl font-black italic uppercase text-white tracking-tighter mb-2">Admin <span className="text-orange-500 underline decoration-orange-900/30">Core</span></h2>
                <p className="text-[11px] text-zinc-500 font-black uppercase tracking-[0.5em]">Overseer Dashboard</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 p-6 rounded-[2rem] shadow-xl text-center hover:-translate-y-1 transition-transform duration-300">
                  <p className="text-[10px] text-zinc-500/80 font-black uppercase tracking-widest mb-2">Total Reviews</p>
                  <p className="text-3xl font-black text-white drop-shadow-md">{adminReviews.length}</p>
                </div>
                <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 p-6 rounded-[2rem] shadow-xl text-center hover:-translate-y-1 transition-transform duration-300">
                  <p className="text-[10px] text-zinc-500/80 font-black uppercase tracking-widest mb-2">Avg Rating</p>
                  <p className="text-3xl font-black text-orange-500 drop-shadow-md">
                    {adminReviews.length > 0 ? (adminReviews.reduce((acc, r) => acc + (r.rating || 0), 0) / adminReviews.length).toFixed(1) : '0.0'}
                  </p>
                </div>
              </div>

              <div className="bg-black/40 backdrop-blur-xl border border-white/5 p-8 rounded-[3rem] shadow-2xl">
                <h3 className="text-xs font-black uppercase text-zinc-500/80 tracking-[0.3em] mb-6 flex items-center gap-2 px-2"><MessageSquare className="w-4 h-4"/> Live Review Feed</h3>
                <div className="space-y-4">
                  {adminReviews.length === 0 ? (
                    <p className="text-center text-zinc-600 text-xs font-mono py-10">No reviews found.</p>
                  ) : (
                    adminReviews.map(rev => (
                      <div key={rev.id} className="bg-zinc-900/50 backdrop-blur-md border border-white/5 p-6 rounded-[2rem] relative hover:bg-zinc-900/80 transition-colors duration-300 shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex gap-1">
                            {[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${rev.rating >= s ? 'text-orange-500 fill-orange-500 drop-shadow-[0_0_5px_rgba(234,88,12,0.5)]' : 'text-zinc-800'}`} />)}
                          </div>
                          <span className="text-[8px] text-zinc-500 font-mono">
                            {rev.timestamp ? new Date(rev.timestamp.seconds * 1000).toLocaleDateString() : 'Just now'}
                          </span>
                        </div>
                        <p className="text-xs text-zinc-300 font-medium leading-relaxed">"{rev.text}"</p>
                        <p className="text-[8px] text-zinc-600 mt-3 truncate font-mono">User: {rev.userId}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              <div className="bg-zinc-900/30 backdrop-blur-md border border-white/5 p-8 rounded-[3rem] text-center opacity-70">
                <Lock className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
                <p className="text-[10px] text-zinc-400 font-black uppercase tracking-widest mb-1">Global Scan Sync Required</p>
                <p className="text-xs font-mono text-zinc-500">Total Scan and Promo Usage queries require backend cloud function integration.</p>
              </div>
            </div>
          )}

          {/* --- VERIFY REPORT PAGE --- */}
          {view === 'VERIFY' && (
            <div className="space-y-8 animate-in fade-in duration-500 pb-20">
              <div className="text-center">
                <h2 className="text-5xl font-black italic uppercase text-white tracking-tighter mb-2 drop-shadow-md">Audit <span className="text-emerald-500 underline decoration-emerald-900/50">Verify</span></h2>
                <p className="text-[11px] text-zinc-500 font-black uppercase tracking-[0.5em]">Global Registry Check</p>
              </div>

              {verifyLoading ? (
                 <div className="py-32 flex flex-col items-center justify-center text-zinc-500">
                   <Loader2 className="w-12 h-12 animate-spin mb-4 text-emerald-500" />
                   <p className="font-black uppercase tracking-widest text-xs">Querying Database...</p>
                 </div>
              ) : verifyData === false ? (
                 <div className="bg-red-950/20 backdrop-blur-xl border border-red-900/30 rounded-[3.5rem] p-10 text-center shadow-2xl">
                   <ShieldAlert className="w-20 h-20 text-red-500 mx-auto mb-6 drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]" />
                   <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Audit Not Found</h3>
                   <p className="text-xs text-red-200/80 font-mono leading-relaxed mb-8">This Report ID ({verifyAuditId}) does not exist in the Haggler database. This document may be forged or fraudulent.</p>
                   <button onClick={(e) => navigateTo(e, 'SCAN', '/')} className="px-10 py-5 bg-red-600 hover:bg-red-500 text-white font-black uppercase text-xs tracking-widest rounded-full transition-all duration-300 shadow-[0_10px_30px_rgba(239,68,68,0.4)] hover:-translate-y-1 hover:shadow-[0_15px_40px_rgba(239,68,68,0.6)]">Run New Scan</button>
                 </div>
              ) : (
                 <div className="bg-white rounded-[2.5rem] p-8 sm:p-10 relative overflow-hidden shadow-2xl">
                   <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-400 to-emerald-600" />
                   <div className="flex justify-between items-start border-b-2 border-black/10 pb-6 mb-6">
                      <div>
                        <h2 className="text-3xl font-black uppercase tracking-tighter text-black">HAGGLER</h2>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 flex items-center gap-1 mt-1"><ShieldCheck className="w-4 h-4"/> Verified Authentic</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] text-zinc-500 uppercase tracking-widest">{t('auditId')}</p>
                        <p className="text-xs font-black text-black">{verifyData.auditId}</p>
                        <p className="text-[8px] text-zinc-400 uppercase tracking-widest mt-1">{t('generated')} {verifyData.auditDate}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-6 text-black font-mono">
                      {verifyData.thumbnail && (
                        <div className="w-full h-40 bg-zinc-100 rounded-2xl overflow-hidden border border-zinc-200 mb-6 shadow-sm relative group cursor-pointer" onClick={() => setExpandedImage(verifyData.thumbnail)}>
                           <img src={verifyData.thumbnail} alt="Scanned Asset" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-300 group-hover:scale-105" />
                           <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none flex flex-col justify-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"><p className="text-[10px] font-black uppercase text-white tracking-widest">{t('tapToExpand')}</p></div>
                        </div>
                      )}
                      <div>
                        <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">{t('targetAsset')}</p>
                        <p className="text-base font-bold">{verifyData.assetName || verifyData.model}</p>
                      </div>
                      
                      <div className="border-y border-dashed border-zinc-300 py-5 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-[11px] uppercase font-bold text-zinc-500">{t('asking')}</span>
                          <span className="text-xl font-black">{verifyData.askingPriceFormatted}</span>
                        </div>
                        
                        {verifyData.itemizedFlaws && verifyData.itemizedFlaws.length > 0 && (
                          <div className="pt-3">
                            <p className="text-[9px] uppercase tracking-widest text-zinc-500 mb-3">{t('adjustments')}</p>
                            <div className="space-y-2">
                              {verifyData.itemizedFlaws.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-start text-sm text-red-600 font-bold bg-red-50 p-2 rounded-lg">
                                  <span className="pr-4">{item.flaw}</span>
                                  <span className="shrink-0">{item.deductionFormatted}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {verifyData.flipperMarginNumeric > 0 && (
                           <div className="pt-3 flex justify-between items-start text-sm text-orange-600 font-bold bg-orange-50 p-2 rounded-lg mt-2">
                             <span className="pr-4 uppercase">{t('flipperMargin')}</span>
                             <span className="shrink-0">-${verifyData.flipperMarginNumeric.toLocaleString()}</span>
                           </div>
                        )}
                      </div>

                      <div className="bg-black p-8 text-center mt-8 rounded-[1.5rem] shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full pointer-events-none"></div>
                        <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-400 mb-2">{t('targetValuation')}</p>
                        <p className="text-4xl font-black text-white tracking-tighter">{verifyData.finalValuationFormatted}</p>
                      </div>

                      {verifyData.sources && verifyData.sources.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-zinc-200">
                          <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-4 font-bold flex items-center justify-center gap-2"><LinkIcon className="w-4 h-4" /> {t('sources')}</p>
                          <ul className="space-y-3">
                            {verifyData.sources.map((src, i) => (
                              <li key={i} className="text-[11px] leading-tight text-center break-words px-2">
                                 <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-bold hover:text-blue-800 transition-colors">
                                   {src.title}
                                 </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="mt-10 pt-6 border-t border-zinc-200">
                          <button onClick={(e) => navigateTo(e, 'SCAN', '/')} className="w-full py-5 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase text-[11px] tracking-widest rounded-2xl transition-all duration-300 shadow-[0_10px_20px_rgba(234,88,12,0.3)] hover:-translate-y-1">
                            {t('new')}
                          </button>
                      </div>
                    </div>
                 </div>
              )}
            </div>
          )}

          {view === 'SUCCESS' && (
            <section className="space-y-10 py-10 animate-in zoom-in-95 duration-700 flex flex-col items-center justify-center min-h-[60vh]">
              <div className="w-32 h-32 bg-emerald-500/20 backdrop-blur-xl border border-emerald-500/30 rounded-[3.5rem] flex items-center justify-center mb-6 shadow-[0_0_80px_rgba(16,185,129,0.3)]"><PartyPopper className="w-16 h-16 text-emerald-400" /></div>
              <div className="text-center space-y-4"><h1 className="text-5xl font-black italic uppercase text-white tracking-tighter drop-shadow-md">Payment <span className="text-emerald-500 underline decoration-emerald-500/50">Confirmed</span></h1><p className="text-[11px] font-medium text-zinc-400/90 uppercase tracking-widest leading-relaxed px-4">Thank you. Your core has been successfully refueled with analysis scans.</p></div>
              <a href="/" onClick={(e) => navigateTo(e, 'SCAN', '/')} className="w-full h-24 bg-orange-600 rounded-[3rem] font-black uppercase text-sm tracking-[0.4em] text-white shadow-[0_15px_40px_rgba(234,88,12,0.4)] hover:bg-orange-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(234,88,12,0.6)] active:scale-95 transition-all duration-300 flex items-center justify-center gap-3">Return to Console <ChevronRight className="w-5 h-5" /></a>
            </section>
          )}

          {view === 'SCAN' && (
            analysis ? (
              <section className="space-y-8 animate-in slide-in-from-bottom-8 duration-700">
                {analysis.redFlags && analysis.redFlags.length > 0 && (
                  <article className="bg-red-950/30 backdrop-blur-xl border border-red-900/50 rounded-[2.5rem] p-8 shadow-[0_0_50px_rgba(220,38,38,0.15)] animate-pulse">
                    <div className="flex items-center gap-3 mb-4 text-red-500"><AlertTriangle className="w-6 h-6" /><h3 className="font-black uppercase tracking-widest text-sm">{t('securityWarning')}</h3></div>
                    <ul className="space-y-2 text-xs font-bold text-red-200/90">
                      {analysis.redFlags.map((flag, idx) => <li key={idx}>• {flag}</li>)}
                    </ul>
                  </article>
                )}

                <article className="bg-zinc-900/60 backdrop-blur-2xl border border-white/5 p-10 rounded-[3.5rem] relative overflow-hidden shadow-2xl">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-bl-[100%] pointer-events-none"></div>
                  <div className={`absolute top-8 right-8 px-5 py-2 rounded-full text-xs font-black italic uppercase tracking-widest text-white shadow-xl backdrop-blur-md ${analysis.dealRating === 'STEAL' ? 'bg-emerald-500/90 border border-emerald-400/50' : analysis.dealRating === 'SCAM' ? 'bg-red-600/90 border border-red-500/50' : analysis.dealRating === 'UNKNOWN' ? 'bg-zinc-500/90 border border-zinc-400/50' : 'bg-orange-600/90 border border-orange-500/50'}`}>{analysis.dealRating} {t('grade')}</div>
                  <h1 className="text-4xl font-black uppercase italic text-white leading-tight mb-4 tracking-tighter drop-shadow-md pr-24">{analysis.assetName || analysis.model}</h1>
                  <div className="flex items-center gap-3 mb-8 text-zinc-400 font-mono text-sm uppercase tracking-widest font-bold"><DollarSign className="w-5 h-5" /> <span>{t('askingLabel')} {analysis.askingPriceFormatted}</span></div>
                  <p className={`text-sm font-medium text-zinc-300/90 italic mb-8 border-l-4 ${theme.border} pl-6 leading-relaxed bg-black/30 backdrop-blur-md p-6 rounded-2xl shadow-inner`}>{analysis.dealAnalysis}</p>
                </article>
                
                {imagePreviews.length > 0 && (
                  <div className="w-full h-[250px] rounded-[3.5rem] border border-white/5 overflow-hidden shadow-2xl relative group cursor-zoom-in" onClick={() => setExpandedImage(imagePreviews[0])}>
                    <img src={imagePreviews[0]} alt="Listing thumbnail" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500"><p className="text-[10px] font-black uppercase text-white/70 tracking-widest">{t('tapToExpand')}</p></div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-5">
                  <div className="bg-zinc-900/60 backdrop-blur-2xl border border-white/5 p-8 rounded-[3rem] flex flex-col items-center justify-center relative overflow-hidden h-full min-h-[160px] shadow-xl hover:-translate-y-1 transition-transform duration-300">
                    <p className="text-[11px] font-black text-zinc-500 uppercase mb-3 italic tracking-widest shrink-0">{t('target')}</p>
                    <div className="w-full max-h-32 overflow-y-auto no-scrollbar flex items-center justify-center">
                      <p className="text-base sm:text-lg font-black text-emerald-400 font-mono tracking-tight underline decoration-emerald-900/80 break-words leading-relaxed text-center drop-shadow-[0_0_15px_rgba(16,185,129,0.3)]">{analysis.finalValuationFormatted}</p>
                    </div>
                  </div>
                  <div className="bg-zinc-900/60 backdrop-blur-2xl border border-white/5 p-8 rounded-[3rem] flex flex-col items-center justify-center text-center shadow-xl hover:-translate-y-1 transition-transform duration-300">
                    <button onClick={() => setShowDossier(true)} className={`flex flex-col items-center gap-3 ${theme.text} hover:scale-105 transition-all duration-300`}>
                      <FileText className={`w-8 h-8 drop-shadow-[0_0_15px_${theme.glow}]`} />
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white drop-shadow-md">{t('report')}</span>
                    </button>
                  </div>
                </div>

                {analysis.itemizedFlaws && analysis.itemizedFlaws.length > 0 && (
                  <article className="bg-black/50 backdrop-blur-xl border border-white/5 rounded-[3rem] p-8 shadow-2xl">
                    <button 
                      className="w-full flex justify-between items-center mb-5 cursor-pointer group bg-transparent" 
                      onClick={() => setFlawsExpanded(!flawsExpanded)}
                      aria-expanded={flawsExpanded}
                    >
                      <p className="text-[10px] font-black uppercase text-zinc-500 tracking-[0.3em] group-hover:text-zinc-400 transition-colors">{t('deductions')} <span className="text-orange-500 ml-1">({analysis.itemizedFlaws.length})</span></p>
                      {analysis.itemizedFlaws.length > 3 && (
                        <div className="p-1.5 bg-zinc-800/50 rounded-full group-hover:bg-zinc-700 transition-colors duration-300">
                          {flawsExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                        </div>
                      )}
                    </button>
                    <div className="space-y-4">
                      {(flawsExpanded ? analysis.itemizedFlaws : analysis.itemizedFlaws.slice(0, 3)).map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs font-mono border-b border-white/5 pb-3 last:border-0 last:pb-0 hover:bg-white/[0.02] p-2 rounded-xl transition-colors">
                          <span className="text-zinc-400/90 pr-4 font-medium">{item.flaw}</span>
                          <span className="text-orange-500 font-bold shrink-0">{item.deductionFormatted}</span>
                        </div>
                      ))}
                    </div>
                    {!flawsExpanded && analysis.itemizedFlaws.length > 3 && (
                      <button 
                        onClick={() => setFlawsExpanded(true)} 
                        className="w-full pt-4 mt-2 border-t border-white/5 cursor-pointer text-center group flex flex-col items-center gap-1 bg-transparent"
                      >
                         <span className="text-[9px] uppercase tracking-[0.3em] font-black text-zinc-500 group-hover:text-orange-500 transition-colors duration-300">+{analysis.itemizedFlaws.length - 3} {t('more')}</span>
                      </button>
                    )}
                  </article>
                )}

                <article className="space-y-5">
                  <div className="flex p-2 bg-zinc-900/60 backdrop-blur-2xl rounded-[2.5rem] border border-white/5 shadow-inner">
                    {['soft', 'medium', 'hard'].map(t => (<button key={t} onClick={() => setTactic(t)} className={`flex-1 py-4 rounded-[2rem] transition-all duration-300 font-black uppercase text-[11px] tracking-[0.2em] ${tactic === t ? `${theme.bg} text-white shadow-[0_10px_20px_${theme.glow}] scale-[1.02]` : 'text-zinc-500 hover:text-white hover:bg-white/5'}`}>{t}</button>))}
                  </div>
                  <div className={`bg-zinc-900/60 backdrop-blur-2xl border border-white/5 rounded-[3.5rem] p-10 sm:p-12 text-base sm:text-lg italic border-l-[12px] ${theme.border} text-zinc-100 shadow-2xl leading-relaxed relative group`}>
                    <div className="absolute top-6 left-8 text-orange-900/30 text-8xl font-serif select-none pointer-events-none -mt-4 -ml-2">“</div>
                    <p className="relative z-10 font-medium text-zinc-200/90">{analysis.scripts[tactic]}</p>
                    <div className="absolute bottom-6 right-8 opacity-10 pointer-events-none"><Gavel className="w-10 h-10" /></div>
                    <button onClick={() => handleCopyText(analysis.scripts[tactic])} className="absolute top-6 right-6 p-4 bg-black/40 hover:bg-white text-zinc-400 hover:text-black rounded-full transition-all duration-300 border border-white/10 backdrop-blur-xl shadow-xl active:scale-95" aria-label="Copy Script">
                      {copied ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </article>

                <article className="bg-zinc-950/60 backdrop-blur-2xl border border-white/5 rounded-[3rem] p-8 shadow-inner">
                  <h3 className="text-[10px] font-black uppercase text-zinc-500 tracking-[0.3em] mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> {t('response')}</h3>
                  <div className="flex gap-3">
                    <input type="text" value={counterInput} onChange={(e) => setCounterInput(e.target.value)} placeholder={t('paste')} className="flex-1 bg-black/50 border border-white/10 rounded-[2rem] px-6 text-sm text-white outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all font-mono" />
                    <button onClick={performCounter} disabled={counterLoading || !counterInput.trim()} className={`w-14 h-14 sm:w-16 sm:h-16 ${theme.bg} rounded-[2rem] flex items-center justify-center text-white shrink-0 shadow-[0_10px_20px_${theme.glow}] hover:scale-105 active:scale-95 disabled:opacity-50 transition-all duration-300`} aria-label="Generate Counter Offer">
                      {counterLoading ? <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" /> : <Send className="w-5 h-5 sm:w-6 sm:h-6 ml-1" />}
                    </button>
                  </div>
                  {counterScript && (
                    <div className="mt-6 p-6 sm:p-8 bg-zinc-900/80 backdrop-blur-md border border-white/10 rounded-[2rem] text-sm sm:text-base text-zinc-200/90 italic font-medium relative group shadow-lg">
                      <p className="pr-12 leading-relaxed">{counterScript}</p>
                      <button onClick={() => handleCopyText(counterScript)} className="absolute top-6 right-6 p-3 bg-black/40 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-all duration-300" aria-label="Copy Counter"><Copy className="w-4 h-4" /></button>
                    </div>
                  )}
                </article>

                <button onClick={() => { setAnalysis(null); setImagePreviews([]); setImages([]); setError(null); setCounterScript(null); setFlawsExpanded(false); }} className="w-full py-12 text-xs font-black uppercase text-zinc-600 hover:text-white transition-all duration-300 tracking-[0.5em] active:scale-95 group"><span className="group-hover:tracking-[0.8em] transition-all duration-500">← {t('new')}</span></button>
                
                {/* --- ADSENSE PUBLISHER CONTENT INJECTION --- */}
                <div className="mt-20 pt-12 border-t border-white/5">
                  <div className="text-center mb-10">
                    <h3 className="text-2xl sm:text-3xl font-black italic uppercase text-white tracking-tighter drop-shadow-md">Marketplace <span className="text-orange-500">Intelligence</span></h3>
                    <p className="text-[10px] text-zinc-500 font-black uppercase tracking-widest mt-2">Latest from Haggler Academy</p>
                  </div>
                  <div className="space-y-5">
                    {ACADEMY_ARTICLES.slice(0, 3).map(article => (
                      <div key={article.id} className="bg-zinc-900/40 backdrop-blur-md border border-white/5 p-8 rounded-[2.5rem] text-left hover:bg-zinc-900/60 transition-colors duration-300 shadow-sm hover:shadow-lg">
                        <h4 className="text-sm sm:text-base font-black italic uppercase text-white mb-3 tracking-tight">{article.title}</h4>
                        <p className="text-xs text-zinc-400/90 font-mono leading-relaxed line-clamp-2 mb-5">{article.preview}</p>
                        <button onClick={(e) => { handleArticleClick(article.id); setView('ACADEMY'); }} className="text-[10px] font-black uppercase tracking-widest text-orange-500 hover:text-orange-400 transition-colors flex items-center gap-1 group">
                          Read Guide <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button onClick={(e) => { setView('ACADEMY'); window.history.pushState({}, '', '/academy'); window.scrollTo({top: 0, behavior: 'smooth'}); }} className="w-full mt-8 py-5 bg-zinc-900/50 backdrop-blur-md border border-white/5 rounded-[2rem] text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all duration-300">
                    View All Articles
                  </button>
                </div>
              </section>
            ) : (
              <section className="space-y-10 animate-in fade-in zoom-in-95 duration-1000">
                <header className="text-center py-10 sm:py-16">
                  <h1 className="text-6xl sm:text-7xl font-black italic uppercase text-white leading-none tracking-tighter mb-6 drop-shadow-xl">{t('win')} <span className={`${theme.text} underline decoration-current/30 drop-shadow-[0_0_20px_${theme.glow}]`}>{t('deal')}</span></h1>
                  <p className="text-[11px] sm:text-xs text-zinc-500 font-black uppercase tracking-[0.6em]">{t('engine')}</p>
                </header>

                <div className="flex gap-2 p-2.5 bg-zinc-900/60 backdrop-blur-xl rounded-[3rem] border border-white/5 shadow-2xl max-w-sm sm:max-w-md mx-auto overflow-x-auto no-scrollbar scroll-smooth snap-x" role="tablist" aria-label="Deal Sector Selection">
                  {[
                    { id: 'general', icon: ShoppingBag, label: language === 'Spanish' ? 'General' : language === 'French' ? 'Général' : language === 'Mandarin' ? '一般' : 'General' },
                    { id: 'auto', icon: Car, label: language === 'Spanish' ? 'Auto' : language === 'French' ? 'Auto' : language === 'Mandarin' ? '汽车' : 'Automotive' },
                    { id: 'real_estate', icon: Building2, label: language === 'Spanish' ? 'Inmuebles' : language === 'French' ? 'Immobilier' : language === 'Mandarin' ? '房地产' : 'Real Estate' },
                    { id: 'luxury', icon: Gem, label: language === 'Spanish' ? 'Lujo' : language === 'French' ? 'Luxe' : language === 'Mandarin' ? '奢华' : 'Luxury' }
                  ].map(sector => (
                    <button 
                      key={sector.id} 
                      onClick={() => setDealSector(sector.id)} 
                      role="tab"
                      aria-selected={dealSector === sector.id}
                      className={`flex-1 min-w-[85px] py-4 px-2 rounded-[2.5rem] transition-all duration-300 flex flex-col items-center gap-2 snap-center ${dealSector === sector.id ? 'bg-zinc-800 text-white shadow-lg border border-white/10 scale-105' : 'text-zinc-500 hover:text-white hover:bg-white/5'}`}
                    >
                      <sector.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                      <span className="font-black uppercase text-[8px] sm:text-[9px] tracking-[0.1em] text-center leading-tight">{sector.label}</span>
                    </button>
                  ))}
                </div>
                
                <div className="flex p-2.5 bg-zinc-900/60 backdrop-blur-xl rounded-[3rem] border border-white/5 shadow-2xl w-full max-w-xs sm:max-w-sm mx-auto" role="group" aria-label="Strategy Selection">
                  <button onClick={() => setBuyingStrategy('buy')} className={`flex-1 py-4 rounded-[2.5rem] transition-all duration-300 flex flex-col items-center gap-1.5 ${buyingStrategy === 'buy' ? 'bg-zinc-800 text-white shadow-lg border border-white/10 scale-105' : 'text-zinc-500 hover:text-white hover:bg-white/5'}`}>
                    <Briefcase className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="font-black uppercase text-[9px] tracking-[0.2em]">{t('buy')}</span>
                  </button>
                  <button onClick={() => setBuyingStrategy('sell')} className={`flex-1 py-4 rounded-[2.5rem] transition-all duration-300 flex flex-col items-center gap-1.5 ${buyingStrategy === 'sell' ? `${theme.lightBg} ${theme.text} shadow-[0_0_20px_${theme.glow}] border ${theme.border} scale-105` : 'text-zinc-500 hover:text-white hover:bg-white/5'}`}>
                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="font-black uppercase text-[9px] tracking-[0.2em]">{t('resell')}</span>
                  </button>
                </div>

                <div className="space-y-6">
                  {loading ? (
                    <div className={`flex flex-col items-center justify-center gap-6 w-full h-72 border-4 rounded-[4rem] relative overflow-hidden transition-all duration-700 ${theme.border} bg-black/60 backdrop-blur-md shadow-inner`}>
                      <div className={`absolute top-0 left-0 w-full h-3 ${theme.bg} shadow-[0_0_60px_15px_current] animate-[pulse_1s_ease-in-out_infinite]`} style={{ color: theme.glow }} />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80" />
                      <div className="flex flex-col items-center gap-8 z-10">
                        <Activity className={`w-16 h-16 ${theme.text} animate-bounce drop-shadow-[0_0_15px_current]`} />
                        <div className={`text-xs font-mono uppercase tracking-[0.4em] ${theme.text} text-center px-4 font-bold`}>{scannerText}</div>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onDrop={handleDrop} 
                      onDragOver={handleDragOver} 
                      className={`flex flex-col items-center justify-center gap-6 w-full h-72 border-[3px] border-dashed border-white/20 rounded-[4rem] cursor-pointer bg-zinc-900/30 backdrop-blur-sm hover:bg-zinc-900/60 hover:border-white/40 transition-all duration-500 group overflow-hidden relative shadow-inner`}
                    >
                      <input type="file" multiple accept="image/*" onChange={handleFileUpload} className="hidden" id="cam-core-main" aria-label="Upload Listing Screenshots" />
                      <label htmlFor="cam-core-main" className="absolute inset-0 w-full h-full cursor-pointer z-10" aria-label="Upload Area Dropzone"></label>
                      <Camera className={`w-16 h-16 text-zinc-600 group-hover:${theme.text} group-hover:scale-110 transition-all duration-500 z-0 pointer-events-none drop-shadow-md`} />
                      <div className="text-center px-6 z-0 pointer-events-none transition-all duration-500 group-hover:-translate-y-2">
                        <p className="text-lg font-black uppercase text-zinc-400 group-hover:text-white italic tracking-[0.2em] mb-3 drop-shadow-sm">{t('upload')}</p>
                        <p className="text-[10px] sm:text-xs text-zinc-600 font-medium uppercase tracking-widest leading-relaxed px-4 group-hover:text-zinc-400">{t('drop')}</p>
                      </div>
                    </div>
                  )}
                  {imagePreviews.length > 0 && !loading && (
                    <div className="flex gap-5 overflow-x-auto pb-6 scrollbar-hide px-3 animate-in slide-in-from-bottom-4 duration-500">
                      {imagePreviews.map((p, i) => (
                        <div key={i} className="relative w-32 h-32 rounded-[2rem] border-2 border-white/10 overflow-hidden shrink-0 shadow-2xl group hover:border-orange-500 transition-colors duration-300">
                          <img src={p} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110" alt={`Upload preview ${i + 1}`} />
                          <button onClick={() => { setImagePreviews(pv=>pv.filter((_,idx)=>idx!==i)); setImages(im=>im.filter((_,idx)=>idx!==i)); }} className="absolute inset-0 bg-red-600/0 hover:bg-red-600/80 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center text-white backdrop-blur-sm" aria-label="Remove Image"><Trash2 className="w-8 h-8 drop-shadow-md" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={performScan} disabled={loading} className={`w-full h-24 ${theme.bg} rounded-[3rem] font-black uppercase tracking-[0.4em] text-white flex items-center justify-center gap-5 active:scale-95 shadow-[0_15px_40px_${theme.glow}] transition-all duration-300 hover:shadow-[0_20px_50px_${theme.glow}] hover:-translate-y-1`}>
                  {loading ? <Loader2 className="w-8 h-8 animate-spin" /> : <Search className="w-8 h-8" />}
                  <span className="drop-shadow-md">{loading ? t('init') : t('analyze')}</span>
                </button>
                {error && <div className="p-8 bg-red-950/40 backdrop-blur-md border border-red-900/50 text-red-400 text-xs font-black uppercase text-center rounded-[2.5rem] shadow-xl animate-in slide-in-from-top-4 duration-300 leading-relaxed tracking-widest">{error}</div>}
              </section>
            )
          )}

          {/* --- WATCHLIST / CRM --- */}
          {view === 'WATCHLIST' && (
            <section className="space-y-8 animate-in fade-in duration-500">
              <header className="text-center"><h1 className="text-5xl font-black italic uppercase text-white tracking-tighter mb-2 drop-shadow-md">{t('vault')} <span className="text-orange-500 underline decoration-orange-900/50">{t('vaultSpan')}</span></h1></header>
              {watchlist.length === 0 ? <div className="py-32 flex flex-col items-center justify-center text-zinc-700 opacity-50"><History className="w-20 h-20 mb-6 drop-shadow-md" /><p className="italic font-black uppercase text-sm tracking-[0.4em]">{t('empty')}</p></div> : (
                <div className="space-y-6 px-1 pb-20">
                  {watchlist.map(item => (
                    <div key={item.id} className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 p-8 sm:p-10 rounded-[3.5rem] transition-all duration-300 flex flex-col group relative overflow-hidden shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:border-white/10 hover:bg-zinc-900/80">
                      <div className="flex justify-between items-center cursor-pointer active:scale-[0.98] transition-transform relative z-10" onClick={async (e) => { 
                          const loadedItem = { ...item };
                          if (!loadedItem.auditId) {
                            loadedItem.auditId = 'HGLR-ARC-' + Math.random().toString(36).substring(2, 8).toUpperCase();
                            loadedItem.auditDate = 'Archived Record';
                            if (db && user) {
                               await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'watchlist', item.id), { auditId: loadedItem.auditId, auditDate: loadedItem.auditDate });
                               await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'reports', loadedItem.auditId), { ...loadedItem, timestamp: serverTimestamp(), creatorId: user.uid });
                            }
                          }
                          setAnalysis(loadedItem); setImagePreviews([]); setImages([]); setCounterScript(null); setFlawsExpanded(false); setView('SCAN'); 
                        }}>
                        <div className="flex flex-1 items-center gap-5 sm:gap-6">
                           {item.thumbnail && (
                             <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl border border-white/10 overflow-hidden shrink-0 shadow-lg">
                               <img src={item.thumbnail} alt="Asset" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110" />
                             </div>
                           )}
                           <div className="flex-1 overflow-hidden pr-4">
                             <div className={`inline-block px-4 py-1.5 rounded-full text-[9px] font-black italic uppercase mb-3 text-white shadow-md ${item.dealRating === 'STEAL' ? 'bg-emerald-500' : item.dealRating === 'SCAM' ? 'bg-red-600' : item.dealRating === 'UNKNOWN' ? 'bg-zinc-500' : 'bg-orange-600'}`}>{item.dealRating} {t('grade')}</div>
                             <h3 className="text-xl sm:text-2xl font-black truncate italic text-white tracking-tight drop-shadow-sm">{item.assetName || item.model}</h3>
                             <p className="text-xs sm:text-sm text-emerald-400 font-mono font-bold italic truncate max-w-[250px] mt-2 drop-shadow-sm">{t('targetLabel')} {item.finalValuationFormatted || item.valuation}</p>
                           </div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'watchlist', item.id)); }} className="opacity-0 group-hover:opacity-100 p-5 hover:text-red-500 transition-all duration-300 text-zinc-500 bg-black/40 backdrop-blur-sm rounded-[2rem] border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 hover:scale-110 active:scale-95 absolute right-6 top-1/2 -translate-y-1/2" aria-label="Delete saved scan"><Trash2 className="w-6 h-6" /></button>
                      </div>
                      
                      {/* P&L CRM BLOCK */}
                      <div className="pt-8 mt-8 border-t border-white/5 bg-black/20 backdrop-blur-md -mx-8 sm:-mx-10 -mb-8 sm:-mb-10 p-8 sm:p-10 flex gap-4 sm:gap-5 items-end relative z-10 transition-colors duration-300 group-hover:bg-black/30">
                        <div className="flex-1">
                          <label className="text-[9px] uppercase font-black text-zinc-500 tracking-widest block mb-3 pl-1">{t('bought')}</label>
                          <input type="number" defaultValue={item.boughtFor || ''} onBlur={(e) => updateFinancials(item.id, 'boughtFor', e.target.value)} className="w-full bg-black/60 border border-white/5 rounded-2xl p-4 text-sm text-white font-mono outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all shadow-inner hover:border-white/20" placeholder="0" />
                        </div>
                        <div className="flex-1">
                          <label className="text-[9px] uppercase font-black text-zinc-500 tracking-widest block mb-3 pl-1">{t('sold')}</label>
                          <input type="number" defaultValue={item.soldFor || ''} onBlur={(e) => updateFinancials(item.id, 'soldFor', e.target.value)} className="w-full bg-black/60 border border-white/5 rounded-2xl p-4 text-sm text-white font-mono outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all shadow-inner hover:border-white/20" placeholder="0" />
                        </div>
                        <div className="flex-1 bg-zinc-950/80 backdrop-blur-md border border-white/5 rounded-2xl p-4 text-center shadow-lg relative overflow-hidden group/profit">
                           <div className={`absolute inset-0 opacity-0 group-hover/profit:opacity-10 transition-opacity duration-500 ${((item.soldFor || 0) - (item.boughtFor || 0)) >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                           <label className="text-[9px] uppercase font-black text-zinc-500 tracking-widest block mb-2 relative z-10">{t('profit')}</label>
                           <span className={`text-base font-black font-mono relative z-10 drop-shadow-md ${((item.soldFor || 0) - (item.boughtFor || 0)) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              ${((item.soldFor || 0) - (item.boughtFor || 0)).toLocaleString()}
                           </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* --- PUBLIC SCRAPBOOK FEED --- */}
          {view === 'FEED' && (
            <section className="space-y-8 animate-in fade-in duration-500 pb-20">
              <header className="text-center">
                <h1 className="text-5xl font-black italic uppercase text-white tracking-tighter mb-2 drop-shadow-md">{t('feed')} <span className="text-orange-500 underline decoration-orange-900/50">{t('feedSpan')}</span></h1>
                <p className="text-[11px] text-zinc-500 font-black uppercase tracking-[0.5em]">{t('appraisals')}</p>
              </header>
              <div className="grid grid-cols-1 gap-6">
                {publicFeed.map((item, idx) => (
                  <div key={idx} className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 p-8 rounded-[3rem] shadow-xl relative overflow-hidden flex flex-col group hover:border-orange-500/40 hover:-translate-y-1 transition-all duration-300">
                     <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-bl-[100%] pointer-events-none transition-transform duration-500 group-hover:scale-110"></div>
                     <div className="flex justify-between items-start mb-6 gap-4 relative z-10">
                       <div className="flex items-center gap-4 flex-1 overflow-hidden">
                         {item.thumbnail && (
                           <div className="w-16 h-16 rounded-2xl border border-white/10 overflow-hidden shrink-0 shadow-lg">
                             <img src={item.thumbnail} alt="Asset" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110" />
                           </div>
                         )}
                         <h3 className="text-xl font-black italic text-white truncate flex-1 drop-shadow-sm">{item.assetName}</h3>
                       </div>
                       <div className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest text-white shadow-md shrink-0 backdrop-blur-md ${item.dealRating === 'STEAL' ? 'bg-emerald-500/90 border border-emerald-400/30' : item.dealRating === 'SCAM' ? 'bg-red-600/90 border border-red-500/30' : item.dealRating === 'UNKNOWN' ? 'bg-zinc-500/90 border border-zinc-400/30' : 'bg-orange-600/90 border border-orange-500/30'}`}>{item.dealRating} {t('grade')}</div>
                     </div>
                     <div className="flex justify-between items-end mt-auto pt-5 border-t border-white/5 relative z-10">
                       <div>
                         <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2 pl-1">{t('asking')}</p>
                         <p className="text-base font-mono text-zinc-400 line-through decoration-red-500/60 font-medium">{item.askingPriceFormatted}</p>
                       </div>
                       <div className="text-right">
                         <p className="text-[10px] uppercase tracking-widest text-emerald-500 font-bold mb-2 pr-1">{t('trueVal')}</p>
                         <p className="text-2xl font-black font-mono text-emerald-400 drop-shadow-sm">{item.finalValuationFormatted}</p>
                       </div>
                     </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* --- ACADEMY / SEO SECTION --- */}
          {view === 'ACADEMY' && (
            <section className="space-y-10 animate-in fade-in duration-500 pb-20">
              <header className="text-center">
                <h1 className="text-5xl font-black italic uppercase text-white tracking-tighter mb-2 drop-shadow-md">Haggler <span className="text-orange-500 underline decoration-orange-900/50">Academy</span></h1>
                <p className="text-[11px] text-zinc-500 font-black uppercase tracking-[0.5em]">{t('engine')}</p>
              </header>

              {!activeArticle ? (
                <div className="space-y-6">
                  {ACADEMY_ARTICLES.map(article => (
                    <a 
                      key={article.id} 
                      href={`/academy/${article.id}`}
                      onClick={(e) => { e.preventDefault(); handleArticleClick(article.id); }} 
                      className="block bg-zinc-900/60 backdrop-blur-xl border border-white/5 p-8 sm:p-10 rounded-[3rem] shadow-xl cursor-pointer hover:border-orange-500/40 hover:bg-zinc-900/80 hover:-translate-y-1 transition-all duration-300 group"
                    >
                      <h2 className="text-xl sm:text-2xl font-black italic text-white uppercase mb-4 leading-tight group-hover:text-orange-400 transition-colors drop-shadow-sm">{article.title}</h2>
                      <p className="text-sm text-zinc-400/90 font-medium leading-relaxed mb-6 line-clamp-3 group-hover:text-zinc-300 transition-colors">{article.preview}</p>
                      <div className="flex items-center text-[10px] font-black uppercase tracking-widest text-orange-500 group-hover:text-white transition-colors">
                        Read Article <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <article className="animate-in slide-in-from-right-8 duration-500">
                  <a 
                    href="/academy"
                    onClick={(e) => { e.preventDefault(); setActiveArticle(null); window.history.pushState({}, '', '/academy'); window.scrollTo({top: 0, behavior: 'smooth'}); }} 
                    className="mb-8 flex items-center text-[11px] font-black uppercase tracking-widest text-zinc-500 hover:text-white transition-colors group w-max"
                  >
                    <div className="bg-zinc-800/50 p-2 rounded-full mr-3 group-hover:bg-zinc-700 transition-colors">
                       <ChevronRight className="w-4 h-4 rotate-180" /> 
                    </div>
                    Back to Academy
                  </a>
                  <div className="bg-zinc-900/60 backdrop-blur-2xl border border-white/5 p-8 sm:p-12 rounded-[3.5rem] shadow-2xl text-zinc-200">
                    <h1 className="text-3xl sm:text-4xl font-black italic text-white uppercase mb-8 leading-tight drop-shadow-md">{ACADEMY_ARTICLES.find(a => a.id === activeArticle)?.title}</h1>
                    <div className="space-y-6 text-sm sm:text-base leading-relaxed font-medium opacity-90 text-justify">
                      {ACADEMY_ARTICLES.find(a => a.id === activeArticle)?.content.split('\n\n').map((paragraph, idx) => (
                        <p key={idx} className="bg-black/20 p-6 rounded-2xl border border-white/5 shadow-inner">{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </article>
              )}
            </section>
          )}

          {/* --- REWARDS & REFERRALS SECTION --- */}
          {view === 'REWARDS' && (
            <section className="space-y-8 animate-in fade-in duration-500 pb-20">
              <header className="text-center">
                <h1 className="text-5xl font-black italic uppercase text-white tracking-tighter mb-2 drop-shadow-md">Rewards <span className="text-orange-500 underline decoration-orange-900/50">Hub</span></h1>
                <p className="text-[11px] text-zinc-500 font-black uppercase tracking-[0.5em]">Earn Free Scans</p>
              </header>

              <article className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 p-8 sm:p-10 rounded-[3rem] shadow-xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 bg-orange-600/20 rounded-2xl border border-orange-500/30">
                     <Award className="w-6 h-6 text-orange-500" />
                  </div>
                  <h2 className="text-xl font-black uppercase italic tracking-widest text-white drop-shadow-sm">Creator Codes</h2>
                </div>
                <p className="text-sm text-zinc-400/90 font-medium mb-8 leading-relaxed">Have a promo code from TikTok, YouTube, or a friend? Enter it here to claim your free scan credits.</p>
                <div className="flex gap-3 sm:gap-4">
                  <input 
                    type="text" 
                    value={promoInput} 
                    onChange={(e) => setPromoInput(e.target.value)} 
                    placeholder="ENTER CODE" 
                    className="flex-1 bg-black/50 border border-white/10 rounded-[2rem] px-6 text-sm text-white uppercase tracking-widest outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all font-black shadow-inner" 
                  />
                  <button onClick={handleRedeemPromo} className="px-6 sm:px-8 h-14 sm:h-16 bg-orange-600 rounded-[2rem] font-black uppercase text-[10px] sm:text-xs tracking-[0.2em] text-white active:scale-95 transition-all duration-300 hover:bg-orange-500 shadow-[0_10px_20px_rgba(234,88,12,0.3)] hover:shadow-[0_15px_30px_rgba(234,88,12,0.5)]">
                    Redeem
                  </button>
                </div>
                {promoMessage && (
                  <div className={`mt-6 p-4 text-xs font-black uppercase tracking-widest text-center rounded-2xl animate-in fade-in slide-in-from-top-2 border backdrop-blur-md ${promoMessage.type === 'error' ? 'text-red-400 bg-red-950/30 border-red-900/50' : 'text-emerald-400 bg-emerald-950/30 border-emerald-900/50'}`}>
                    {promoMessage.text}
                  </div>
                )}
              </article>

              {user && (
                <article className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 p-8 sm:p-10 rounded-[3rem] shadow-xl relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-orange-600/10 rounded-bl-[100%] z-0 transition-transform duration-700 group-hover:scale-110 pointer-events-none" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 bg-orange-600/20 rounded-2xl border border-orange-500/30">
                         <Share2 className="w-6 h-6 text-orange-500" />
                      </div>
                      <h2 className="text-xl font-black uppercase italic tracking-widest text-white drop-shadow-sm">Refer & Earn</h2>
                    </div>
                    <p className="text-sm text-zinc-400/90 font-medium mb-8 leading-relaxed">Share Haggler with your friends. Anyone who uses your unique link will get <strong className="text-orange-400 px-1">+5 extra credits</strong> when they initialize their core.</p>
                    <div className="bg-black/50 p-5 sm:p-6 rounded-[2rem] border border-white/10 flex justify-between items-center gap-4 shadow-inner">
                      <p className="text-[10px] sm:text-xs text-zinc-400 font-mono truncate select-all">{`https://hagglerusa.com/?ref=${user.uid.slice(0,8)}`}</p>
                      <button onClick={() => handleCopyText(`https://hagglerusa.com/?ref=${user.uid.slice(0,8)}`, "Link")} className="p-4 bg-zinc-800 rounded-xl text-white hover:bg-orange-600 transition-colors duration-300 shrink-0 shadow-md active:scale-95" aria-label="Copy Referral Link">
                        <Copy className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </article>
              )}

              <article className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 p-8 sm:p-10 rounded-[3rem] shadow-xl">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-600/20 rounded-2xl border border-orange-500/30">
                       <Star className="w-6 h-6 text-orange-500" />
                    </div>
                    <h2 className="text-xl font-black uppercase italic tracking-widest text-white drop-shadow-sm">Feedback</h2>
                  </div>
                  {!hasReviewed && <span className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full shadow-sm">+3 Credits</span>}
                </div>
                
                {hasReviewed ? (
                  <div className="text-center py-10 opacity-70 bg-black/20 rounded-[2rem] border border-white/5 shadow-inner">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4 drop-shadow-[0_0_15px_rgba(16,185,129,0.4)]" />
                    <p className="text-xs font-black uppercase tracking-widest text-zinc-300">Review Received. Thank you!</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-zinc-400/90 font-medium mb-8 leading-relaxed">How much money did Haggler save you? Leave a review to earn 3 free scan credits.</p>
                    <div className="flex justify-center gap-3 sm:gap-4 mb-8 bg-black/30 p-6 rounded-[2rem] border border-white/5 shadow-inner" role="radiogroup" aria-label="Star Rating">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star 
                          key={star} 
                          onClick={() => setReviewRating(star)}
                          role="radio"
                          aria-checked={reviewRating >= star}
                          className={`w-10 h-10 sm:w-12 sm:h-12 cursor-pointer transition-all duration-300 ${reviewRating >= star ? 'text-orange-500 fill-orange-500 scale-110 drop-shadow-[0_0_15px_rgba(234,88,12,0.5)]' : 'text-zinc-700 hover:text-zinc-500 hover:scale-105'}`} 
                        />
                      ))}
                    </div>
                    <textarea 
                      value={reviewText} 
                      onChange={(e) => setReviewText(e.target.value)} 
                      placeholder="Tell us about your deal..." 
                      className="w-full h-32 bg-black/50 border border-white/10 rounded-[2rem] p-6 text-sm text-white outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all font-medium resize-none mb-6 shadow-inner" 
                      aria-label="Review Text"
                    />
                    <button 
                      onClick={handleSubmitReview} 
                      disabled={reviewRating === 0 || reviewSubmitted} 
                      className="w-full h-16 bg-zinc-800/80 backdrop-blur rounded-[2rem] font-black uppercase text-xs tracking-[0.3em] text-white disabled:opacity-50 enabled:active:scale-95 enabled:hover:bg-orange-600 transition-all duration-300 shadow-lg enabled:hover:shadow-[0_10px_30px_rgba(234,88,12,0.4)] border border-white/5"
                    >
                      {reviewSubmitted ? 'Submitting...' : 'Submit Review'}
                    </button>
                  </>
                )}
              </article>
            </section>
          )}

          {/* --- PRICING --- */}
          {view === 'PRICING' && (
            <section className="space-y-10 py-10 animate-in slide-in-from-right-8 duration-500">
              <header className="text-center mb-10">
                <div className="w-24 h-24 bg-orange-600/20 backdrop-blur-md border border-orange-500/40 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-[0_0_50px_rgba(234,88,12,0.3)]"><Zap className="w-12 h-12 text-orange-500 fill-current" /></div>
                <h1 className="text-5xl font-black uppercase italic tracking-tighter text-white text-center underline decoration-orange-600/40 drop-shadow-md">Refuel Core</h1>
              </header>
              <div className="space-y-8 px-2 pb-20">
                {[
                  {id:'STARTER', c:10, p:'4.99', d:['10 Analysis Scans','Market Value Analysis']},
                  {id:'PRO', c:25, p:'9.99', d:['25 Analysis Scans','Advanced Flaw Tracking','Cloud Vault Storage'], active:true},
                  {id:'ELITE', c:60, p:'19.99', d:['60 Analysis Scans','Priority Processing','Elite Aggression Scripts']}
                ].map(tier => (
                  <article key={tier.id} className={`bg-zinc-900/60 backdrop-blur-2xl border ${tier.active ? 'border-orange-500 shadow-[0_0_60px_rgba(234,88,12,0.15)] scale-[1.02]' : 'border-white/5'} p-10 sm:p-12 rounded-[3.5rem] flex flex-col gap-8 relative hover:bg-zinc-900/80 transition-all duration-300 shadow-2xl`}>
                    {tier.active && <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[10px] font-black uppercase tracking-[0.4em] px-8 py-3 rounded-full z-20 shadow-[0_10px_20px_rgba(234,88,12,0.4)] border border-orange-400/50 backdrop-blur-md">Most Requested</div>}
                    <div className="flex justify-between items-end mt-4">
                      <div>
                        <h2 className={`text-3xl sm:text-4xl font-black uppercase italic tracking-tighter transition-colors ${tier.active ? 'text-white' : 'text-white/80 group-hover:text-white'}`}>{tier.id}</h2>
                        <p className="text-xs text-orange-500 font-black tracking-[0.4em] mt-2 drop-shadow-sm">{tier.c} CREDITS</p>
                      </div>
                      <p className="text-5xl sm:text-6xl font-black text-white italic tracking-tighter drop-shadow-md">${tier.p}</p>
                    </div>
                    <div className="h-px w-full bg-white/10 my-2"></div>
                    <ul className="space-y-5 py-2">
                      {tier.d.map((li, idx) => (
                        <li key={idx} className="text-sm font-bold text-zinc-300/90 flex items-center gap-4">
                          <CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0 drop-shadow-[0_0_8px_rgba(234,88,12,0.4)]" /> 
                          <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => handlePurchase(tier.id)} className={`w-full h-20 rounded-[2rem] font-black uppercase text-sm tracking-[0.4em] flex items-center justify-center gap-4 active:scale-95 transition-all duration-300 mt-4 ${tier.active ? 'bg-orange-600 text-white shadow-[0_15px_30px_rgba(234,88,12,0.4)] hover:bg-orange-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(234,88,12,0.5)]' : 'bg-zinc-800/80 text-white hover:bg-zinc-700 border border-white/5'}`}>
                      BUY NOW <ExternalLink className="w-5 h-5" />
                    </button>
                  </article>
                ))}
              </div>
            </section>
          )}

          <footer className="mt-32 pt-16 border-t border-white/5 flex flex-col items-center text-center pb-12 relative z-50">
             <div className="w-full flex justify-center mb-12 bg-transparent overflow-visible opacity-80 hover:opacity-100 transition-opacity duration-500">
                <img 
                  src="/LunchboxLogotransparent.png" 
                  alt="LUNCHBOX Operating Core" 
                  className="h-32 w-auto brightness-110 contrast-100 drop-shadow-[0_0_30px_rgba(255,255,255,0.15)] relative z-[100]" 
                />
             </div>
             
             <div className="max-w-xs sm:max-w-md mx-auto text-[10px] text-zinc-500/80 text-justify mb-10 font-medium leading-relaxed">
               <p>Haggler is an advanced AI negotiation assistant designed for any transaction. Upload screenshots to instantly generate live market comps, identify asset flaws, audit seller claims, and create soft, medium, or hard negotiation scripts to secure the lowest possible purchase price on vehicles, real estate, electronics, and local inventory.</p>
             </div>

             <nav className="space-y-12 w-full">
                <div className="flex justify-center gap-10 text-[10px] text-zinc-500 font-black uppercase tracking-[0.2em]">
                  <button onClick={() => setShowLegal('TOS')} className="hover:text-white transition-colors">Terms</button>
                  <button onClick={() => setShowLegal('PRIVACY')} className="hover:text-white transition-colors">Privacy</button>
                  <button className="hover:text-white transition-colors cursor-default">Support</button>
                </div>
                
                <div className="text-[9px] text-zinc-600/80 uppercase space-y-4 font-mono font-bold tracking-[0.1em] px-10 leading-relaxed border-t border-white/5 pt-10 mx-auto max-w-sm">
                  <p>© 2026 LUNCHBOX OPERATING CORE | ALL RIGHTS RESERVED</p>
                  <p>5001 LINCOLN DR. KNOXVILLE TN 37914</p>
                  <div className="flex items-center justify-center gap-2 mt-6 bg-black/40 py-3 rounded-full border border-white/5 w-max mx-auto px-6 shadow-inner">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
                    <span className="text-[8px] tracking-widest text-zinc-400">SYSTEM STATUS: <span className="text-emerald-400">{sysStatus}</span></span>
                  </div>
                </div>
             </nav>
          </footer>
        </main>

        <AdMobileBottom />

        <nav className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md lg:max-w-xl bg-zinc-950/80 backdrop-blur-3xl border border-white/10 rounded-full h-20 flex items-center justify-around px-3 z-[110] shadow-[0_20px_50px_rgba(0,0,0,0.8)]" aria-label="Main Navigation">
          {[
            { id: 'SCAN', icon: Search, label: t('navHaggle'), path: '/' },
            { id: 'WATCHLIST', icon: LayoutDashboard, label: t('navVault'), path: '/vault' },
            { id: 'FEED', icon: Globe, label: t('navFeed'), path: '/feed' },
            { id: 'REWARDS', icon: Gift, label: t('navRewards'), path: '/rewards' },
            { id: 'PRICING', icon: Zap, label: t('navRefuel'), path: '/refuel' }
          ].map(tab => (
            <a 
              key={tab.id} 
              href={tab.path}
              onClick={(e) => navigateTo(e, tab.id, tab.path)} 
              className={`group flex flex-col items-center gap-1.5 transition-all duration-300 px-4 py-2 rounded-full ${view === tab.id ? 'text-orange-500 bg-white/5 scale-105' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}`}
              aria-current={view === tab.id ? 'page' : undefined}
            >
              <tab.icon className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-300 ${view === tab.id ? 'drop-shadow-[0_0_15px_rgba(234,88,12,0.8)] stroke-[2.5px]' : ''}`} />
              <span className={`text-[8px] uppercase font-black tracking-[0.1em] transition-all duration-300 ${view === tab.id ? 'opacity-100' : 'opacity-60 group-hover:opacity-100'}`}>{tab.label}</span>
            </a>
          ))}
        </nav>
        
        {checkoutMsg && (
          <div className="fixed bottom-32 left-1/2 -translate-x-1/2 w-[92%] max-w-sm p-6 bg-emerald-600/95 backdrop-blur-xl border border-emerald-400/50 rounded-[2.5rem] text-sm text-white text-center font-black uppercase shadow-[0_20px_50px_rgba(16,185,129,0.4)] z-[150] flex flex-col items-center justify-center animate-in slide-in-from-bottom-10 duration-500" role="alert">
            {checkoutMsg !== "GENERATING HAGGLER REPORT..." && checkoutMsg !== "COPYING HAGGLER REPORT..." && <PartyPopper className="w-8 h-8 animate-bounce mb-2 text-emerald-200" />}
            {(checkoutMsg === "GENERATING HAGGLER REPORT..." || checkoutMsg === "COPYING HAGGLER REPORT...") && <Loader2 className="w-8 h-8 animate-spin mb-2 text-emerald-200" />}
            <span className="tracking-[0.2em]">{checkoutMsg}</span>
          </div>
        )}
      </div>

      {/* --- LANGUAGE SELECTION MODAL --- */}
      {showLangModal && (
        <div className="fixed inset-0 z-[400] bg-black/80 backdrop-blur-2xl flex items-end justify-center p-4 sm:items-center transition-all duration-300" onClick={() => setShowLangModal(false)}>
          <div className="w-full max-w-sm bg-zinc-900/90 backdrop-blur-3xl border border-white/10 rounded-[3rem] p-8 shadow-2xl relative overflow-hidden animate-in slide-in-from-bottom-10 sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-black italic uppercase text-white tracking-tighter drop-shadow-md">Language</h3>
              <button onClick={() => setShowLangModal(false)} className="p-3 bg-white/5 rounded-full text-white hover:bg-red-500/20 hover:text-red-500 transition-all"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              {['English', 'Spanish', 'French', 'Mandarin'].map(lang => (
                <button 
                  key={lang} 
                  onClick={() => { setLanguage(lang); setShowLangModal(false); }}
                  className={`w-full p-5 rounded-[2rem] font-black uppercase tracking-widest text-sm flex justify-between items-center transition-all duration-300 ${language === lang ? 'bg-orange-600 text-white shadow-[0_10px_20px_rgba(234,88,12,0.4)] scale-[1.02]' : 'bg-black/50 border border-white/5 text-zinc-400 hover:text-white hover:bg-zinc-800 hover:border-white/20'}`}
                >
                  {lang}
                  {language === lang && <CheckCircle2 className="w-6 h-6 text-white drop-shadow-md" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- APPRAISAL DOSSIER MODAL (RECEIPT VIEW) --- */}
      {showDossier && analysis && (
        <div className="fixed inset-0 z-[400] bg-black/95 backdrop-blur-3xl flex items-center justify-center p-4">
          <style>{`
            @media print {
              body * { visibility: hidden !important; }
              #dossier-capture-area, #dossier-capture-area * { visibility: visible !important; }
              #dossier-capture-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px !important; margin: 0 !important; background: white !important; box-shadow: none !important; border: none !important; }
              .no-print { display: none !important; }
            }
          `}</style>
          <div className="w-full max-w-md lg:max-w-lg bg-white rounded-none border-t-8 border-t-black shadow-2xl overflow-y-auto max-h-[90vh] no-scrollbar relative flex flex-col animate-in zoom-in-95 duration-500">
            <article id="dossier-capture-area" className="p-8 sm:p-10 bg-white pb-8 flex-1 shrink-0">
              <header className="flex justify-between items-start border-b-2 border-black pb-4 mb-8">
                <div>
                  <h2 className="text-4xl font-black uppercase tracking-tighter text-black leading-none">HAGGLER</h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-2">{t('officialReport')}</p>
                </div>
                <button onClick={() => setShowDossier(false)} className="p-3 bg-zinc-100 rounded-full text-black hover:bg-zinc-200 transition-colors no-print" data-html2canvas-ignore="true" aria-label="Close Report"><X className="w-5 h-5" /></button>
              </header>
              
              <div className="space-y-8 text-black font-mono">
                {verifyData && verifyData.thumbnail && (
                  <div className="w-full h-40 bg-zinc-100 rounded-2xl overflow-hidden border border-zinc-200 mb-6 shadow-inner relative">
                     <img src={verifyData.thumbnail} alt="Scanned Asset" className="w-full h-full object-cover opacity-90" />
                     <div className="absolute inset-0 bg-black/5 pointer-events-none"></div>
                  </div>
                )}
                <div>
                  <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 font-bold">{t('targetAsset')}</h3>
                  <p className="text-lg font-black leading-tight">{analysis.assetName || analysis.model}</p>
                </div>
                
                {/* MATHEMATICAL BREAKDOWN */}
                <div className="border-y-2 border-dashed border-zinc-300 py-6 space-y-4">
                  <div className="flex justify-between items-center bg-zinc-50 p-4 rounded-xl">
                    <span className="text-xs uppercase font-bold text-zinc-600 tracking-wider">{t('asking')}</span>
                    <span className="text-2xl font-black">{analysis.askingPriceFormatted}</span>
                  </div>
                  
                  {analysis.itemizedFlaws && analysis.itemizedFlaws.length > 0 && (
                    <div className="pt-4">
                      <h4 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3 font-bold">{t('adjustments')}</h4>
                      <div className="space-y-2.5">
                        {analysis.itemizedFlaws.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-start text-sm text-red-600 font-bold bg-red-50 p-3 rounded-lg border border-red-100">
                            <span className="pr-4 leading-snug">{item.flaw}</span>
                            <span className="shrink-0">{item.deductionFormatted}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* FLIPPER MARGIN RENDER */}
                  {analysis.flipperMarginNumeric > 0 && (
                     <div className="pt-4 flex justify-between items-start text-sm text-orange-600 font-bold bg-orange-50 p-3 rounded-lg border border-orange-100 mt-2">
                       <span className="pr-4 uppercase leading-snug">{t('flipperMargin')}</span>
                       <span className="shrink-0">-${analysis.flipperMarginNumeric.toLocaleString()}</span>
                     </div>
                  )}
                </div>

                <div className="bg-black p-8 text-center mt-10 rounded-[1.5rem] shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full pointer-events-none"></div>
                  <h3 className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3 font-bold">{t('targetValuation')}</h3>
                  <p className="text-5xl font-black text-white tracking-tighter drop-shadow-lg">{analysis.finalValuationFormatted}</p>
                </div>
                
                {/* VERIFIED SOURCES INJECTION */}
                {analysis.sources && analysis.sources.length > 0 && (
                  <div className="mt-10 pt-6 border-t-2 border-zinc-100">
                    <h3 className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4 font-bold flex items-center justify-center gap-2"><LinkIcon className="w-4 h-4 text-blue-500"/> {t('sources')}</h3>
                    <ul className="space-y-3 bg-zinc-50 p-4 rounded-xl border border-zinc-200">
                      {analysis.sources.map((src, i) => (
                        <li key={i} className="text-xs leading-tight text-center break-words px-2">
                           <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-bold hover:text-blue-800 underline decoration-blue-200 hover:decoration-blue-600 transition-colors">
                             {src.title}
                           </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* AUTHENTICITY STAMP & QR CODE */}
                <div className="mt-10 pt-8 border-t-2 border-zinc-100 flex justify-between items-end bg-zinc-50 p-6 rounded-2xl">
                   <div>
                      <p className="text-[9px] text-zinc-500 uppercase tracking-widest flex items-center gap-1.5 font-bold mb-1"><QrCode className="w-4 h-4 text-black" /> {t('auditId')}</p>
                      <p className="text-sm font-black text-black">{analysis.auditId}</p>
                      <p className="text-[9px] text-zinc-500 uppercase tracking-widest mt-2 font-bold">{t('generated')} <span className="text-black">{analysis.auditDate}</span></p>
                   </div>
                   <div className="w-20 h-20 border-2 border-black rounded-lg bg-white p-1.5 shadow-md transform rotate-2">
                      <img src={`https://quickchart.io/qr?text=https://hagglerusa.com/verify/${analysis.auditId}&size=150&margin=0`} alt="QR Code Verification" className="w-full h-full" crossOrigin="anonymous" />
                   </div>
                </div>
                
                <p className="text-[9px] text-center text-zinc-400 uppercase tracking-[0.2em] mt-10 pb-4 font-bold">{t('generatedBy')}</p>
              </div>
            </article>
            
            {/* STICKY FOOTER ACTIONS FOR MODAL */}
            <div className="sticky bottom-0 left-0 right-0 p-5 bg-zinc-100 border-t border-zinc-300 flex gap-4 no-print z-10 w-full mt-auto shadow-[0_-10px_20px_rgba(0,0,0,0.05)]" data-html2canvas-ignore="true">
              <button onClick={() => window.print()} className="flex-1 py-5 bg-white border border-zinc-300 hover:bg-zinc-50 hover:border-zinc-400 text-black font-black uppercase text-[11px] tracking-widest rounded-2xl flex items-center justify-center gap-2 transition-all shadow-sm active:scale-95">
                <Printer className="w-4 h-4" /> {t('print')}
              </button>
              <button onClick={handleCopyDossier} className="flex-1 py-5 bg-zinc-900 hover:bg-black text-white font-black uppercase text-[11px] tracking-widest rounded-2xl flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95">
                <Copy className="w-4 h-4" /> {t('copy')}
              </button>
              <button onClick={handleShareDossier} className="flex-1 py-5 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase text-[11px] tracking-widest rounded-2xl flex items-center justify-center gap-2 transition-all shadow-[0_10px_20px_rgba(234,88,12,0.3)] hover:-translate-y-1 active:scale-95">
                <Share2 className="w-4 h-4" /> {t('share')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- EXPANDED IMAGE MODAL --- */}
      {expandedImage && (
        <div className="fixed inset-0 z-[500] bg-black/98 backdrop-blur-3xl flex items-center justify-center p-0 animate-in fade-in duration-300" onClick={handleCloseExpanded}>
          <button onClick={handleCloseExpanded} className="absolute top-8 right-8 p-4 bg-zinc-900/80 backdrop-blur border border-white/10 rounded-full text-white hover:bg-red-600 hover:border-red-500 transition-all duration-300 z-[510] shadow-2xl active:scale-95" aria-label="Close Image">
            <X className="w-6 h-6" />
          </button>
          <div className="w-full h-full flex items-center justify-center overflow-auto no-scrollbar relative p-4">
            <img src={expandedImage} alt="Expanded View" onClick={(e) => { e.stopPropagation(); setIsZoomed(!isZoomed); }} className={`transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] shadow-[0_0_100px_rgba(0,0,0,0.8)] rounded-xl ${isZoomed ? 'w-[250%] max-w-none cursor-zoom-out' : 'w-full h-full object-contain max-h-[90vh] cursor-zoom-in'}`} />
          </div>
        </div>
      )}

      {/* ACCOUNT PROFILE MODAL */}
      {showProfileModal && (
        <div className="fixed inset-0 z-[400] bg-black/80 backdrop-blur-2xl flex items-center justify-center p-4 transition-all duration-300" onClick={() => setShowProfileModal(false)}>
          <div className="w-full max-w-sm bg-zinc-900/90 backdrop-blur-3xl border border-white/10 rounded-[3rem] p-10 shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-600 to-amber-500" />
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-black italic uppercase text-white tracking-tighter drop-shadow-md">Account</h3>
              <button onClick={() => setShowProfileModal(false)} className="p-3 bg-white/5 rounded-full text-white hover:bg-red-500/20 hover:text-red-500 transition-all"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="space-y-6">
              <div className="text-center p-8 bg-black/40 border border-white/5 rounded-[2.5rem] shadow-inner">
                {user && !user.isAnonymous && user.photoURL ? (
                  <img src={user.photoURL} alt="Profile" className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-orange-500/50 object-cover shadow-lg" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-20 h-20 bg-zinc-800/50 rounded-full mx-auto mb-4 border-2 border-white/10 flex items-center justify-center shadow-inner">
                    <User className="w-10 h-10 text-zinc-500" />
                  </div>
                )}
                <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-[0.2em] mb-2 font-bold">Status</p>
                <p className="text-base font-black text-white truncate px-2 drop-shadow-md">
                  {user?.isAnonymous ? 'Guest User' : (user?.displayName || user?.email || 'Authenticated User')}
                </p>
                {user?.isAnonymous && <p className="text-[10px] text-zinc-400/80 mt-4 font-medium leading-relaxed bg-white/5 p-3 rounded-xl border border-white/5">Sign in to save your credits and vault scans across all your devices.</p>}
              </div>

              {user?.isAnonymous ? (
                <button onClick={handleGoogleSignIn} className="w-full h-16 bg-white text-black rounded-[2rem] font-black uppercase text-xs tracking-[0.2em] hover:bg-zinc-200 active:scale-95 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg">
                  <LogIn className="w-5 h-5 text-orange-600" /> Sign In with Google
                </button>
              ) : (
                <button onClick={handleSignOut} className="w-full h-16 bg-red-600/10 border border-red-500/30 rounded-[2rem] font-black uppercase text-xs tracking-[0.2em] text-red-500 active:scale-95 transition-all duration-300 flex items-center justify-center gap-3 hover:bg-red-600 hover:text-white hover:border-red-500 shadow-sm">
                  <LogOut className="w-5 h-5" /> Sign Out
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ADMIN PORTAL */}
      {showAdminLogin && (
        <div className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-3xl flex items-center justify-center p-6 text-center animate-in fade-in duration-300">
           <div className="w-full max-w-md bg-zinc-900/90 backdrop-blur-xl border border-white/10 rounded-[4rem] p-12 shadow-[0_50px_100px_rgba(0,0,0,0.8)] relative overflow-hidden animate-in zoom-in-95 duration-500">
             <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-red-600 to-orange-600" />
             <div className="w-24 h-24 bg-red-500/20 border border-red-500/30 rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-[0_0_30px_rgba(239,68,68,0.3)]">
                <ShieldAlert className="w-12 h-12 text-red-500" />
             </div>
             <h3 className="font-black italic uppercase text-white tracking-[0.4em] text-3xl mb-10 drop-shadow-md">Admin Access</h3>
             <input type="password" value={adminPass} onChange={(e) => setAdminPass(e.target.value)} placeholder="PASSCODE" className="w-full bg-black/60 border-2 border-white/10 rounded-[2rem] p-8 text-center text-white mb-10 outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 font-mono text-3xl tracking-[0.8em] placeholder:text-[10px] placeholder:tracking-widest shadow-inner transition-all duration-300" />
             <div className="flex gap-4">
               <button onClick={() => setShowAdminLogin(false)} className="flex-1 h-20 bg-zinc-800/80 backdrop-blur border border-white/5 rounded-[2rem] font-black uppercase text-xs text-white hover:bg-zinc-700 transition-colors active:scale-95">Abort</button>
               <button onClick={handleAdminSubmit} className="flex-1 h-20 bg-orange-600 rounded-[2rem] font-black uppercase text-xs text-white shadow-[0_10px_20px_rgba(234,88,12,0.4)] hover:bg-orange-500 hover:-translate-y-1 active:scale-95 transition-all duration-300">Authorize</button>
             </div>
           </div>
        </div>
      )}
      {showLegal && <LegalModal type={showLegal} onClose={() => setShowLegal(null)} />}
    </div>
  );
};

export default App;
